<script setup lang="ts">
import StickerPurchaseEditor from "../components/StickerPurchaseEditor.vue";
import { computed, onMounted, reactive, ref } from "vue";
import { onBeforeRouteLeave } from "vue-router";
import BoundCustomParameterEditor from "../components/BoundCustomParameterEditor.vue";
import DetailSectionTemplateTools from "../components/DetailSectionTemplateTools.vue";
import type { SectionCode, SectionDraft, SectionTemplate, SectionOperation, TemplateImage, ImageReference } from "../api/detail-section-templates";
import { sectionContent } from "../api/detail-section-templates";
import ProductServiceInfoEditor from "../components/ProductServiceInfoEditor.vue";
import CustomServiceTemplates from "../components/CustomServiceTemplates.vue";
import { serviceFields, type ServiceField } from "../api/service-templates";
import { activateSku, archiveProduct, checkPublish, createImage, createDetailBlock, createDetailSection, createPackageUnit, createProduct, createQuantityTier, createInlineSku, deactivateImage, deactivateDetailBlock, deactivateDetailSection, deactivateSku, deactivatePackageUnit, deactivateQuantityTier, deleteImage, getProduct, listFreightTemplates, listCustomOptionPresets, getAdminCustomizationConfig, listProductCategories, listProducts, publishProduct, unpublishProduct, reorderProductGallery, updateDetailBlock, updateDetailSection, updateProduct, updateProductParameters, updateProductMaterial, updatePackageUnitName, updateQuantityTier, updateSku, uploadProductImage, type PackageUnit, type FreightTemplate, type ProductCategory, type ProductDetail, type ProductDetailBlock, type ProductImage, type ProductSummary, type PublishCheck, type SkuView, type AdminCustomizationConfig, type CustomOptionGroup, type CustomOptionPreset, type CustomOptionInputType } from "../api/products";
import {
  isCreateFreightRequired,
  resetNewProductFreightOnModeChange,
  resolveCreateFreightTemplateId,
} from "./product-form";
import { resolveProductGalleryDropSource } from "./product-gallery-drag";


function inheritsServiceField(key: ServiceField): boolean {
  return selected.value?.customizationEnabled === true && (selected.value.serviceTemplate?.inheritFields.includes(key) ?? false);
}
const serviceFieldsDirty = computed(() => Boolean(selected.value) && serviceFields.some(({ key }) =>
  (productForm[key].trim() || null) !== (selected.value?.[key] ?? null)));
function serviceBindingSaved(product: ProductDetail): void {
  selected.value = product;
}
const independentServiceEditor = ref<InstanceType<typeof ProductServiceInfoEditor> | null>(null);
const customParameterEditor = ref<InstanceType<typeof BoundCustomParameterEditor> | null>(null);
function canLeaveEditors(): boolean {
  return [...detailTemplateTools.values()].every(tool=>tool.canLeave()) && (customParameterEditor.value?.canLeave() ?? true) && (independentServiceEditor.value?.canLeave() ?? true);
}
onBeforeRouteLeave(canLeaveEditors);
function customParametersSaved(config: AdminCustomizationConfig): void { customConfig.value = config; syncCustomConfigDrafts(config); }
function independentServiceSaved(product: ProductDetail): void {
  selected.value = product;
  for (const { key } of serviceFields) productForm[key] = product[key] ?? "";
  productForm.craftsmanshipDescription = product.craftsmanshipDescription ?? "";
}
const products = ref<ProductSummary[]>([]);
const selected = ref<ProductDetail | null>(null);
const categories = ref<ProductCategory[]>([]);
const freightTemplates = ref<FreightTemplate[]>([]);
const busy = ref(false);
const message = ref("");
const publishResult = ref<PublishCheck | null>(null);
const customConfig = ref<AdminCustomizationConfig | null>(null);
const customConfigLoading = ref(false);
const customPresets = ref<CustomOptionPreset[]>([]);
const customPresetsLoading = ref(false);
const customPresetsError = ref("");
const selectedCustomPresetIds = ref<string[]>([]);
const newCustomGroupFormOpen = ref(false);
let customConfigRequestVersion = 0;

interface CustomGroupDraft {
  code: string;
  name: string;
  inputType: CustomOptionInputType;
  required: boolean;
  unit: string;
  min: number | null;
  max: number | null;
  step: number | null;
  sortOrder: number;
  dimensionFields: CustomOptionDimensionFields;
}

interface CustomOptionDimensionFields {
  length: boolean;
  width: boolean;
  height: boolean;
  flatLength: boolean;
  flatWidth: boolean;
}

const DEFAULT_DIMENSION_FIELDS: CustomOptionDimensionFields = {
  length: true,
  width: true,
  height: true,
  flatLength: true,
  flatWidth: true,
};

interface CustomValueDraft {
  code: string;
  name: string;
  sortOrder: number;
}

const customGroupDrafts = reactive<Record<string, CustomGroupDraft>>({});
const customValueDrafts = reactive<Record<string, CustomValueDraft>>({});
const expandedCustomGroups = reactive<Record<string, boolean>>({});
const newCustomGroup = reactive<CustomGroupDraft>({
  code: "",
  name: "",
  inputType: "SINGLE_SELECT",
  required: false,
  unit: "",
  min: null,
  max: null,
  step: 1,
  sortOrder: 0,
  dimensionFields: { ...DEFAULT_DIMENSION_FIELDS },
});
const newCustomValues = reactive<Record<string, CustomValueDraft>>({});

const productForm = reactive({
  isHot: false,
  productNo: "",
  name: "",
  subtitle: "",
  categoryId: "",
  freightTemplateId: "",
  material: "",
  detailContent: "",
  customizationEnabled: false,
  shippingLeadTime: "",
  shippingDescription: "",
  freeShippingDescription: "",
  guaranteeDescription: "",
  craftsmanshipDescription: "",
  invoiceDescription: "",
  customOrderEnabled: false,
  customizationMoq: null as number | null,
  sortOrder: 0,
});

function handleProductTypeChange(): void {
  if (!selected.value) {
    productForm.freightTemplateId = resetNewProductFreightOnModeChange(
      productForm.customizationEnabled,
      productForm.freightTemplateId,
    );
  }
}
const skuForms = reactive<
  Record<
    string,
    {
      unitType: "PACK" | "BOX";
      parentUnitId: string;
      parentUnitCount: number;
      tierPieceQty: number;
      tierPrice: number;
      dimensionNote: string;
      allowBreakPackage: boolean;
      retailEnabled: boolean;
      sortOrder: number;
    }
  >
>({});

function categoryMatchesCurrentType(category: ProductCategory): boolean {
  return category.scope === "BOTH" || category.scope === (productForm.customizationEnabled ? "CUSTOM" : "STOCK");
}

const rootCategories = computed(() =>
  categories.value
    .filter((category) => category.parentId === null && categoryMatchesCurrentType(category))
    .sort((left, right) => left.sortOrder - right.sortOrder || left.id.localeCompare(right.id)),
);

const hasActiveCustomMaterialGroup = computed(() => {
  if (!selected.value?.customizationEnabled || !customConfig.value) return false;
  return customConfig.value.optionGroups.some((group) =>
    group.isActive && (group.code.trim().toUpperCase() === "MATERIAL" || group.name.trim() === "材质"),
  );
});

const selectedRootCategoryId = computed<string>({
  get() {
    const category = categories.value.find((item) => item.id === productForm.categoryId);
    return category?.parentId ?? category?.id ?? "";
  },
  set(rootId) {
    const firstChild = categories.value
      .filter((category) => category.parentId === rootId && categoryMatchesCurrentType(category))
      .sort((left, right) => left.sortOrder - right.sortOrder || left.id.localeCompare(right.id))[0];
    productForm.categoryId = firstChild?.id ?? rootId;
  },
});

const selectedCategoryChildren = computed(() =>
  categories.value
    .filter((category) => category.parentId === selectedRootCategoryId.value && categoryMatchesCurrentType(category))
    .sort((left, right) => left.sortOrder - right.sortOrder || left.id.localeCompare(right.id)),
);

const activeSkuId = ref("");
interface NewSkuDraft {
  id: number;
  specName: string;
  tierPieceQty: number;
  priceYuan: number;
  error: string;
}

const newSkuDrafts = ref<NewSkuDraft[]>([]);
let nextInlineSkuDraftId = 1;
interface NewQuantityTierDraft {
  tierPieceQty: number;
  priceYuan: number;
}

const inlineTierDrafts = reactive<Record<string, NewQuantityTierDraft>>({});
const inlinePackageSettings = reactive({
  unitName: "",
  basePieceCount: null as number | null,
});
const inlinePackageLocked = ref(false);
const inlinePackageConflict = ref(false);
const productSwitcher = ref<HTMLDetailsElement | null>(null);
const activeSku = computed(() =>
  selected.value?.skus.find((sku) => sku.id === activeSkuId.value)
  ?? selected.value?.skus[0]
  ?? null,
);
const activePieceUnit = computed(() =>
  activeSku.value?.packageUnits.find((unit) => unit.unitType === "PIECE" && unit.status === "ACTIVE")
    ?? activeSku.value?.packageUnits.find((unit) => unit.unitType === "PIECE")
    ?? null,
);
const baseUnitName = computed(() => activePieceUnit.value?.unitName ?? "个");
const activeSkus = computed(() => selected.value?.skus.filter((sku) => sku.status === "ACTIVE") ?? []);
const productImages = computed(() =>
  [...(selected.value?.images ?? [])]
    .filter((image) => image.skuId == null && image.status === "ACTIVE" && ["MAIN", "ACTUAL", "DIMENSION"].includes(image.type))
    .sort((left, right) => {
      const leftOrder = Number.isFinite(Number(left.sortOrder)) ? Number(left.sortOrder) : Number.MAX_SAFE_INTEGER;
      const rightOrder = Number.isFinite(Number(right.sortOrder)) ? Number(right.sortOrder) : Number.MAX_SAFE_INTEGER;
      return leftOrder - rightOrder || left.id.localeCompare(right.id);
    }),
);
const detailImages = computed(() =>
  [...(selected.value?.images ?? [])]
    .filter((image) => image.skuId == null && image.type === "DETAIL")
    .sort((left, right) => {
      const leftOrder = Number.isFinite(Number(left.sortOrder)) ? Number(left.sortOrder) : Number.MAX_SAFE_INTEGER;
      const rightOrder = Number.isFinite(Number(right.sortOrder)) ? Number(right.sortOrder) : Number.MAX_SAFE_INTEGER;
      return leftOrder - rightOrder || left.id.localeCompare(right.id);
    }),
);

const draggingProductImageId = ref<string | null>(null);
const productImageDropTargetId = ref<string | null>(null);

const editableDetailSections = [
  {
    code: "INTRODUCTION" as const,
    label: "商品介绍",
    description: "维护客户在小程序商品详情中看到的商品介绍文字。",
    sortOrder: 0,
  },
  {
    code: "SHIPPING_INFO" as const,
    label: "发货说明",
    description: "维护现货商品的发货时效、运费与服务说明。",
    sortOrder: 1,
  },
  {
    code: "FILE_SPEC" as const,
    label: "传图规范",
    description: "告诉客户可接受的设计文件、图片清晰度和传图注意事项。",
    sortOrder: 2,
  },
  {
    code: "CUSTOM_SERVICE" as const,
    label: "包装与售后",
    description: "维护当前商品的包装说明、收货检查和售后处理方式。",
    sortOrder: 3,
  },
] as const;

type EditableDetailSectionCode = (typeof editableDetailSections)[number]["code"];

const activeDetailSectionCode = ref<EditableDetailSectionCode>("INTRODUCTION");
const visibleEditableDetailSections = computed(() => editableDetailSections.filter((section) =>
  selected.value?.customizationEnabled ? section.code !== "SHIPPING_INFO" : section.code !== "FILE_SPEC",
));
const activeDetailSection = computed(
  () => visibleEditableDetailSections.value.find((item) => item.code === activeDetailSectionCode.value) ?? visibleEditableDetailSections.value[0],
);
const editingDetailBlockKeys = reactive<Record<EditableDetailSectionCode, string[]>>({
  INTRODUCTION: [],
  SHIPPING_INFO: [],
  FILE_SPEC: [],
  CUSTOM_SERVICE: [],
});

interface DetailSectionDraft {
  sectionId: string | null;
  sortOrder: number;
  body: string;
  status: "ACTIVE" | "INACTIVE" | null;
}

interface DetailBlockDraft {
  templateImage?: TemplateImage;
  templateRef?: ImageReference;
  clientKey: string;
  id: string | null;
  isLegacyFallback: boolean;
  legacyBody: string;
  type: ProductDetailBlock["type"];
  title: string;
  body: string;
  productImageId: string;
  sortOrder: number;
  updatedAt: string | null;
}

let nextDetailDraftKey = 1;
const detailTemplateTools = new Map<SectionCode,InstanceType<typeof DetailSectionTemplateTools>>();
const detailTemplateLocks = reactive<Record<SectionCode,boolean>>({FILE_SPEC:false,CUSTOM_SERVICE:false});
const detailTemplateLocked = computed(()=>Object.values(detailTemplateLocks).some(Boolean));
const templateSectionCodes = computed<SectionCode[]>(()=>selected.value?.customizationEnabled?["FILE_SPEC","CUSTOM_SERVICE"]:["CUSTOM_SERVICE"]);
function registerDetailTemplateTool(code:SectionCode,value:unknown):void {
  if(value)detailTemplateTools.set(code,value as InstanceType<typeof DetailSectionTemplateTools>);
  else detailTemplateTools.delete(code);
}
function detailTemplateDraft(code:SectionCode):SectionDraft {
  return {code,blocks:detailBlockDrafts[code].map(block=>({type:block.type,title:block.title.trim()||null,body:block.body.trim()||null,sortOrder:block.sortOrder,
    image:block.type==="TEXT"?null:block.productImageId?{kind:"PRODUCT",imageId:block.productImageId}:block.templateRef??null}))};
}
function importDetailTemplate(code:SectionCode,value:{template:SectionTemplate;revision:string}):void {
  const blocks=value.template.snapshot.blocks.map(block=>({ ...emptyDetailBlockDraft(block.type,block.sortOrder),title:block.title??"",body:block.body??"",
    ...(block.image?{templateImage:block.image,templateRef:{kind:"TEMPLATE" as const,templateId:value.template.id,version:value.template.version,imageKey:block.image.key}}:{}) }));
  detailBlockDrafts[code].splice(0,detailBlockDrafts[code].length,...blocks);
  removedDetailBlockIds[code].splice(0,removedDetailBlockIds[code].length);
  editingDetailBlockKeys[code].splice(0,editingDetailBlockKeys[code].length);
  activeDetailSectionCode.value=code;
}
function captureDetailTemplateUndo(code:SectionCode):()=>void {
  const productId=selected.value?.id;
  const blocks=JSON.parse(JSON.stringify(detailBlockDrafts[code])) as DetailBlockDraft[];
  const removed=[...removedDetailBlockIds[code]],editing=[...editingDetailBlockKeys[code]];
  return ()=>{
    if(selected.value?.id!==productId)throw new Error("商品已切换，无法撤销原商品草稿");
    detailBlockDrafts[code].splice(0,detailBlockDrafts[code].length,...blocks);
    removedDetailBlockIds[code].splice(0,removedDetailBlockIds[code].length,...removed);
    editingDetailBlockKeys[code].splice(0,editingDetailBlockKeys[code].length,...editing);
    activeDetailSectionCode.value=code;
  };
}
async function readbackDetailTemplate(result:SectionOperation):Promise<void> {
  const product=await getProduct(result.productId);
  if(selected.value?.id!==result.productId)throw new Error("商品已切换，请回到原商品核对栏目保存结果");
  selected.value={...selected.value,images:product.images,detailSections:product.detailSections};
  syncDetailSectionDrafts(selected.value,result.code);
  message.value="栏目模板已独立保存";
}
function detailDraftImage(block:DetailBlockDraft):{url:string;altText:string|null}|undefined {
  return block.templateImage??detailBlockImage(block.productImageId);
}

const detailSectionDrafts = reactive<Record<EditableDetailSectionCode, DetailSectionDraft>>({
  INTRODUCTION: {
    sectionId: null,
    sortOrder: 0,
    body: "",
    status: null,
  },
  SHIPPING_INFO: {
    sectionId: null,
    sortOrder: 1,
    body: "",
    status: null,
  },
  FILE_SPEC: {
    sectionId: null,
    sortOrder: 2,
    body: "",
    status: null,
  },
  CUSTOM_SERVICE: {
    sectionId: null,
    sortOrder: 3,
    body: "",
    status: null,
  },
});

const detailBlockDrafts = reactive<Record<EditableDetailSectionCode, DetailBlockDraft[]>>({
  INTRODUCTION: [],
  SHIPPING_INFO: [],
  FILE_SPEC: [],
  CUSTOM_SERVICE: [],
});
const removedDetailBlockIds = reactive<Record<EditableDetailSectionCode, string[]>>({
  INTRODUCTION: [],
  SHIPPING_INFO: [],
  FILE_SPEC: [],
  CUSTOM_SERVICE: [],
});
const draggingDetailImage = ref<{
  code: EditableDetailSectionCode;
  clientKey: string;
} | null>(null);
const detailImageDropTarget = ref<{
  code: EditableDetailSectionCode;
  clientKey: string;
} | null>(null);

const activeDetailImages = computed(() => detailImages.value.filter((image) => image.status === "ACTIVE"));

function emptyDetailBlockDraft(type: ProductDetailBlock["type"], sortOrder: number): DetailBlockDraft {
  return {
    clientKey: `new-${nextDetailDraftKey++}`,
    id: null,
    isLegacyFallback: false,
    legacyBody: "",
    type,
    title: "",
    body: "",
    productImageId: "",
    sortOrder,
    updatedAt: null,
  };
}

function draftFromDetailBlock(block: ProductDetailBlock, sortOrder: number): DetailBlockDraft {
  return {
    clientKey: block.id,
    id: block.id,
    isLegacyFallback: false,
    legacyBody: "",
    type: block.type,
    title: block.title?.trim() ?? "",
    body: block.body?.trim() ?? "",
    productImageId: block.image?.id ?? "",
    sortOrder,
    updatedAt: block.updatedAt ?? null,
  };
}

function detailBlockImage(imageId: string): ProductImage | undefined {
  return activeDetailImages.value.find((image) => image.id === imageId);
}

function isDetailImageBlock(block: Pick<DetailBlockDraft, "type">): boolean {
  return block.type === "IMAGE";
}

function isDetailBlockEditing(code: EditableDetailSectionCode, block: DetailBlockDraft): boolean {
  return editingDetailBlockKeys[code].includes(block.clientKey);
}

function toggleDetailBlockEditing(code: EditableDetailSectionCode, block: DetailBlockDraft): void {
  const keys = editingDetailBlockKeys[code];
  const index = keys.indexOf(block.clientKey);
  if (index >= 0) keys.splice(index, 1);
  else keys.push(block.clientKey);
}

function detailBlockSummary(block: DetailBlockDraft): string {
  const summary = block.body.replace(/\s+/g, " ").trim();
  if (!summary) return "未填写文字内容";
  return summary.length > 72 ? `${summary.slice(0, 72)}…` : summary;
}

function addDetailBlock(code: EditableDetailSectionCode, type: ProductDetailBlock["type"]): void {
  const drafts = detailBlockDrafts[code];
  const nextSortOrder = drafts.reduce((max, item) => {
    const sortOrder = Number(item.sortOrder);
    return Number.isFinite(sortOrder) ? Math.max(max, sortOrder) : max;
  }, -1) + 1;
  const draft = emptyDetailBlockDraft(type, nextSortOrder);
  drafts.push(draft);
  if (type !== "IMAGE") editingDetailBlockKeys[code].push(draft.clientKey);
}

function removeDetailBlock(code: EditableDetailSectionCode, index: number): void {
  const drafts = detailBlockDrafts[code];
  const [removed] = drafts.splice(index, 1);
  if (removed) {
    const editingIndex = editingDetailBlockKeys[code].indexOf(removed.clientKey);
    if (editingIndex >= 0) editingDetailBlockKeys[code].splice(editingIndex, 1);
  }
  if (removed?.id) removedDetailBlockIds[code].push(removed.id);
  drafts.forEach((draft, draftIndex) => { draft.sortOrder = draftIndex; });
}

function clearDetailImageDrag(): void {
  draggingDetailImage.value = null;
  detailImageDropTarget.value = null;
}

function onDetailImageDragStart(
  code: EditableDetailSectionCode,
  block: DetailBlockDraft,
  event: DragEvent,
): void {
  if (!isDetailImageBlock(block)) return;
  draggingDetailImage.value = { code, clientKey: block.clientKey };
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", block.clientKey);
  }
}

function onDetailImagePointerDown(
  code: EditableDetailSectionCode,
  block: DetailBlockDraft,
  event: MouseEvent,
): void {
  if (event.button !== 0 || !isDetailImageBlock(block)) return;
  draggingDetailImage.value = { code, clientKey: block.clientKey };
  detailImageDropTarget.value = null;
}

function onDetailImagePointerEnter(
  code: EditableDetailSectionCode,
  block: DetailBlockDraft,
): void {
  const dragging = draggingDetailImage.value;
  if (!dragging || dragging.code !== code || !isDetailImageBlock(block)) return;
  detailImageDropTarget.value = { code, clientKey: block.clientKey };
}

function onDetailImagePointerUp(
  code: EditableDetailSectionCode,
  block: DetailBlockDraft,
  event: MouseEvent,
): void {
  if (event.button !== 0) return;
  const dragging = draggingDetailImage.value;
  if (!dragging || dragging.code !== code || !isDetailImageBlock(block)) {
    clearDetailImageDrag();
    return;
  }
  reorderDetailImageBlocks(code, dragging.clientKey, block.clientKey);
  clearDetailImageDrag();
}

function onDetailImageDragOver(
  code: EditableDetailSectionCode,
  block: DetailBlockDraft,
  event: DragEvent,
): void {
  const dragging = draggingDetailImage.value;
  if (!dragging || dragging.code !== code || !isDetailImageBlock(block)) return;
  event.preventDefault();
  if (event.dataTransfer) event.dataTransfer.dropEffect = "move";
  detailImageDropTarget.value = { code, clientKey: block.clientKey };
}

function reorderDetailImageBlocks(
  code: EditableDetailSectionCode,
  sourceKey: string,
  targetKey: string,
): void {
  if (sourceKey === targetKey) return;
  const drafts = detailBlockDrafts[code];
  const imagePositions = drafts
    .map((draft, index) => (isDetailImageBlock(draft) ? index : -1))
    .filter((index) => index >= 0);
  const sourceImageIndex = imagePositions.findIndex((index) => drafts[index].clientKey === sourceKey);
  const targetImageIndex = imagePositions.findIndex((index) => drafts[index].clientKey === targetKey);
  if (sourceImageIndex < 0 || targetImageIndex < 0) return;

  const imageDrafts = imagePositions.map((index) => drafts[index]);
  const [moving] = imageDrafts.splice(sourceImageIndex, 1);
  imageDrafts.splice(targetImageIndex, 0, moving);

  // Keep TEXT/IMAGE_TEXT slots untouched; only the image slots change order.
  imagePositions.forEach((draftIndex, imageIndex) => {
    drafts[draftIndex] = imageDrafts[imageIndex];
  });
  drafts.forEach((draft, draftIndex) => {
    draft.sortOrder = draftIndex;
  });
}

function onDetailImageDrop(
  code: EditableDetailSectionCode,
  block: DetailBlockDraft,
  event: DragEvent,
): void {
  const dragging = draggingDetailImage.value;
  if (!dragging || dragging.code !== code || !isDetailImageBlock(block)) {
    clearDetailImageDrag();
    return;
  }
  event.preventDefault();
  const sourceKey = event.dataTransfer?.getData("text/plain") || dragging.clientKey;
  reorderDetailImageBlocks(code, sourceKey, block.clientKey);
  clearDetailImageDrag();
}

function clearProductImageDrag(): void {
  draggingProductImageId.value = null;
  productImageDropTargetId.value = null;
}

function onProductImageDragStart(image: ProductImage, event: DragEvent): void {
  draggingProductImageId.value = image.id;
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", image.id);
  }
}

function onProductImagePointerDown(image: ProductImage, event: MouseEvent): void {
  if (event.button !== 0) return;
  draggingProductImageId.value = image.id;
  productImageDropTargetId.value = null;
}

function onProductImagePointerEnter(image: ProductImage): void {
  if (draggingProductImageId.value) productImageDropTargetId.value = image.id;
}

function onProductImagePointerUp(image: ProductImage, event: MouseEvent): void {
  if (event.button !== 0) return;
  const sourceId = resolveProductGalleryDropSource(draggingProductImageId.value, image.id);
  clearProductImageDrag();
  if (sourceId) void reorderProductImages(sourceId, image.id);
}

function onProductImageDragOver(image: ProductImage, event: DragEvent): void {
  if (!draggingProductImageId.value) return;
  event.preventDefault();
  if (event.dataTransfer) event.dataTransfer.dropEffect = "move";
  productImageDropTargetId.value = image.id;
}

function onProductImageDrop(image: ProductImage, event: DragEvent): void {
  event.preventDefault();
  // The active ref is the single source of truth. A stale dataTransfer value
  // must not re-submit after the mouse fallback already committed the drop.
  const sourceId = resolveProductGalleryDropSource(draggingProductImageId.value, image.id);
  clearProductImageDrag();
  if (sourceId) void reorderProductImages(sourceId, image.id);
}

async function reorderProductImages(sourceId: string, targetId: string): Promise<void> {
  const imageIds = productImages.value.map((image) => image.id);
  const sourceIndex = imageIds.indexOf(sourceId);
  const targetIndex = imageIds.indexOf(targetId);
  if (sourceIndex < 0 || targetIndex < 0 || sourceIndex === targetIndex) return;
  const [moving] = imageIds.splice(sourceIndex, 1);
  imageIds.splice(targetIndex, 0, moving);
  await run(async () => {
    await reorderProductGallery(selected.value!.id, imageIds);
    await refreshSelected();
    message.value = "商品图片顺序已保存";
  });
}

function nextDetailBlockSortOrder(blocks: ProductDetailBlock[]): number {
  return blocks.reduce((max, block) => {
    const order = Number(block.sortOrder);
    return Number.isFinite(order) ? Math.max(max, order) : max;
  }, -1) + 1;
}

function syncDetailSectionDrafts(product: ProductDetail | null, onlyCode?:SectionCode): void {
  for (const definition of editableDetailSections.filter(item=>!onlyCode||item.code===onlyCode)) {
    if(definition.code==="FILE_SPEC"||definition.code==="CUSTOM_SERVICE")detailTemplateTools.get(definition.code)?.resetDraft();
    const section = product?.detailSections.find((item) => item.code === definition.code) ?? null;
    const textBlock = section?.blocks.find(
      (block) => (block.type === "TEXT" || block.type === "IMAGE_TEXT")
        && block.status !== "INACTIVE"
        && Boolean(block.body?.trim()),
    ) ?? null;
    const legacyIntroduction = definition.code === "INTRODUCTION" && !textBlock
      ? product?.detailContent?.trim() ?? ""
      : "";
    Object.assign(detailSectionDrafts[definition.code], {
      sectionId: section?.id ?? null,
      sortOrder: Number.isFinite(Number(section?.sortOrder)) ? Number(section?.sortOrder) : definition.sortOrder,
      body: textBlock?.body?.trim() ?? legacyIntroduction,
      status: section?.status ?? null,
    });

    const activeBlocks = (section?.blocks ?? [])
      .filter((block) => isActiveDetailBlock(block))
      .filter((block) => block.type === "TEXT" || block.type === "IMAGE" || block.type === "IMAGE_TEXT")
      .sort((left, right) => left.sortOrder - right.sortOrder || left.id.localeCompare(right.id));
    const drafts = activeBlocks.map((block, index) => draftFromDetailBlock(
      block,
      Number.isFinite(Number(block.sortOrder)) ? Number(block.sortOrder) : index,
    ));
    if (definition.code === "INTRODUCTION" && !drafts.length && legacyIntroduction) {
      drafts.push({
        ...emptyDetailBlockDraft("TEXT", 0),
        body: legacyIntroduction,
        isLegacyFallback: true,
        legacyBody: legacyIntroduction,
      });
    }
    detailBlockDrafts[definition.code].splice(0, detailBlockDrafts[definition.code].length, ...drafts);
    editingDetailBlockKeys[definition.code].splice(0, editingDetailBlockKeys[definition.code].length);
    removedDetailBlockIds[definition.code].splice(0, removedDetailBlockIds[definition.code].length);
  }
}

function resetDetailSectionDrafts(): void {
  syncDetailSectionDrafts(null);
}

function isActiveDetailBlock(block: ProductDetailBlock): boolean {
  return block.status !== "INACTIVE";
}

async function saveStructuredDetailSection(code: EditableDetailSectionCode, refresh = true): Promise<void> {
  const product = selected.value;
  const definition = editableDetailSections.find((item) => item.code === code)!;
  const drafts = detailBlockDrafts[code];
  if (!product) {
    message.value = "请先保存商品基础信息，再编辑商品详情。";
    return;
  }
  if(code==="FILE_SPEC"||code==="CUSTOM_SERVICE") {
    if(await detailTemplateTools.get(code)?.saveImported())return;
    if(drafts.some(block=>block.templateRef))throw new Error("请先重新打开栏目模板工具，保留草稿核对后再保存");
  }
  const section = product.detailSections.find((item) => item.code === code) ?? null;
  if (section?.status === "INACTIVE") {
    throw new Error(`${definition.label}已停用，当前不能直接编辑；请先恢复该栏目。`);
  }
  if (!drafts.length && !removedDetailBlockIds[code].length) {
    message.value = `${definition.label}没有待保存的内容块。`;
    return;
  }

  const inputs = drafts.map((draft, index) => {
    const title = draft.title.trim() || null;
    const body = draft.body.trim() || null;
    const productImageId = draft.type === "TEXT" ? null : draft.productImageId || null;
    if (draft.type === "TEXT" && !title && !body) {
      throw new Error(`${definition.label}第 ${index + 1} 个文字块不能为空。`);
    }
    if (draft.type === "IMAGE" && !productImageId) {
      throw new Error(`${definition.label}第 ${index + 1} 个图片块请选择详情图片。`);
    }
    if (draft.type === "IMAGE_TEXT" && (!productImageId || (!title && !body))) {
      throw new Error(`${definition.label}第 ${index + 1} 个图文块需要图片和文字内容。`);
    }
    if (productImageId && !activeDetailImages.value.some((image) => image.id === productImageId)) {
      throw new Error(`${definition.label}第 ${index + 1} 个图片块引用的详情图片不存在或已停用。`);
    }
    return { draft, title, body, productImageId, sortOrder: draft.sortOrder };
  });

  let sectionId = section?.id ?? null;
  let createdSectionId: string | null = null;
  try {
    if (!sectionId && inputs.length) {
      const created = await createDetailSection(product.id, {
        code,
        title: definition.label,
        sortOrder: definition.sortOrder,
      });
      sectionId = created.id;
      createdSectionId = created.id;
    } else if (section && (section.title.trim() !== definition.label || section.sortOrder !== definition.sortOrder)) {
      if (!section.updatedAt) throw new Error("详情栏目缺少并发校验时间，请刷新后重试");
      await updateDetailSection(product.id, section.id, {
        title: definition.label,
        sortOrder: definition.sortOrder,
        expectedUpdatedAt: section.updatedAt,
      });
    }

    if (sectionId) {
      for (const blockId of removedDetailBlockIds[code]) {
        await deactivateDetailBlock(product.id, blockId);
      }
    }

    for (const { draft, title, body, productImageId, sortOrder } of inputs) {
      if (!draft.id) {
        if (!sectionId) throw new Error(`${definition.label}栏目创建失败`);
        await createDetailBlock(product.id, sectionId, {
          type: draft.type,
          title,
          body,
          productImageId,
          sortOrder,
        });
        continue;
      }
      const current = section?.blocks.find((block) => block.id === draft.id);
      if (!current || !isActiveDetailBlock(current)) throw new Error("详情内容块已不存在，请刷新后重试");
      const currentImageId = current.image?.id ?? null;
      const changed = current.type !== draft.type
        || (current.title?.trim() ?? null) !== title
        || (current.body?.trim() ?? null) !== body
        || currentImageId !== productImageId
        || current.sortOrder !== sortOrder;
      if (!changed) continue;
      if (!current.updatedAt) throw new Error("详情内容块缺少并发校验时间，请刷新后重试");
      await updateDetailBlock(product.id, draft.id, {
        type: draft.type,
        title,
        body,
        productImageId,
        sortOrder,
        expectedUpdatedAt: current.updatedAt,
      });
    }
  } catch (error) {
    if (createdSectionId) {
      try {
        await deactivateDetailSection(product.id, createdSectionId);
      } catch {
        // 保留原始保存错误，避免清理失败覆盖真正原因。
      }
    }
    throw new Error(`${definition.label}保存失败：${saveErrorMessage(error)}`);
  }

  if (refresh) {
    await refreshSelected();
    message.value = `${definition.label}保存成功`;
  }
}

async function persistAllDetailSections(): Promise<boolean> {
  const product = selected.value;
  if (!product) {
    throw new Error("请先保存商品基础信息，再编辑商品详情。");
  }
  const changedCodes = editableDetailSections
    .map((definition) => definition.code)
    .filter((code) => detailSectionHasUnsavedChanges(code));
  if (!changedCodes.length) return false;

  const failures: string[] = [];
  let savedCount = 0;
  for (const code of changedCodes) {
    try {
      await saveStructuredDetailSection(code, false);
      savedCount += 1;
    } catch (error) {
      const definition = editableDetailSections.find((item) => item.code === code)!;
      failures.push(`${definition.label}：${saveErrorMessage(error)}`);
    }
  }

  if (failures.length) {
    throw new Error(`商品详情部分保存失败（已保存 ${savedCount} 个栏目）：${failures.join("；")}`);
  }
  return true;
}

async function clearDetailSection(code: EditableDetailSectionCode): Promise<void> {
  await run(async () => {
    const product = selected.value;
    const definition = editableDetailSections.find((item) => item.code === code)!;
    if (!product) {
      message.value = "请先保存商品基础信息，再编辑商品详情。";
      return;
    }
    const section = product.detailSections.find((item) => item.code === code) ?? null;
    if (!section || section.status === "INACTIVE") {
      message.value = `${definition.label}当前没有可清空的有效栏目。`;
      return;
    }
    const blocks = section.blocks.filter((block) => isActiveDetailBlock(block));
    if (!blocks.length) {
      message.value = `${definition.label}当前没有可清空的内容块。`;
      return;
    }
    try {
      for (const block of blocks) {
        await deactivateDetailBlock(product.id, block.id);
      }
    } catch (error) {
      throw new Error(`${definition.label}清空失败：${saveErrorMessage(error)}`);
    }
    await refreshSelected();
    message.value = `${definition.label}已清空`;
  });
}

async function syncDetailBlocks(productId: string): Promise<void> {
  const product = selected.value;
  if (!product || product.id !== productId) return;

  // Other columns own their current and historical image references. Never import or reorder them here.
  const otherSectionImageIds = new Set(product.detailSections
    .filter((section) => section.code !== "INTRODUCTION")
    .flatMap((section) => section.blocks)
    .flatMap((block) => block.image ? [block.image.id] : []));
  const activeImages = detailImages.value.filter((image) => image.status === "ACTIVE" && !otherSectionImageIds.has(image.id));
  if (!activeImages.length) return;

  const introduction = product.detailSections.find(
    (section) => section.code === "INTRODUCTION" && section.status !== "INACTIVE",
  );
  if (!introduction) {
    const inactiveIntroduction = product.detailSections.find((section) => section.code === "INTRODUCTION");
    if (inactiveIntroduction) throw new Error("商品详情栏目已停用，请先恢复后再保存详情图片");
  }

  const sectionId = introduction?.id ?? (await createDetailSection(productId, {
    code: "INTRODUCTION",
    title: "商品详情",
    sortOrder: 0,
  })).id;
  const blockByImageId = new Map(
    (introduction?.blocks ?? [])
      .filter((block) => isActiveDetailBlock(block) && block.image)
      .map((block) => [block.image!.id, block]),
  );

  for (const image of activeImages) {
    const block = blockByImageId.get(image.id);
    if (!block) {
      await createDetailBlock(productId, sectionId, {
        type: "IMAGE",
        productImageId: image.id,
        sortOrder: image.sortOrder,
      });
      continue;
    }
    if (block.updatedAt && block.sortOrder !== image.sortOrder) {
      await updateDetailBlock(productId, block.id, {
        sortOrder: image.sortOrder,
        expectedUpdatedAt: block.updatedAt,
      });
    }
  }
}

const productGalleryTypes: ProductImage["type"][] = ["ACTUAL", "DIMENSION", "ACTUAL", "DIMENSION"];
const skuGalleryTypes: ProductImage["type"][] = ["ACTUAL", "DIMENSION", "ACTUAL", "DIMENSION"];

function nextImageType(images: ProductImage[], skuImage: boolean): ProductImage["type"] {
  if (!images.some((image) => image.type === "MAIN")) return "MAIN";
  const types = skuImage ? skuGalleryTypes : productGalleryTypes;
  const nonMainCount = images.filter((image) => image.type !== "MAIN").length;
  return types[Math.min(nonMainCount, types.length - 1)] ?? (skuImage ? "ACTUAL" : "DETAIL");
}

function nextImageSortOrder(images: ProductImage[]): number {
  return images.reduce((max, image) => {
    const order = Number(image.sortOrder);
    return Number.isFinite(order) ? Math.max(max, order) : max;
  }, -1) + 1;
}

function formFor(sku: SkuView) {
  if (!skuForms[sku.id]) {
    const piece = sku.packageUnits.find((unit) => unit.unitType === "PIECE" && unit.status === "ACTIVE");
    skuForms[sku.id] = {
      unitType: "PACK",
      parentUnitId: piece?.id ?? "",
      parentUnitCount: 10,
      tierPieceQty: 50,
      tierPrice: 8900,
      dimensionNote: sku.dimensions.note ?? "",
      allowBreakPackage: sku.allowBreakPackage,
      retailEnabled: sku.retailEnabled,
      sortOrder: sku.sortOrder,
    };
  }
  return skuForms[sku.id];
}

function parentUnits(sku: SkuView, unitType: "PACK" | "BOX"): PackageUnit[] {
  const parentType = unitType === "PACK" ? "PIECE" : "PACK";
  return sku.packageUnits.filter((unit) => unit.unitType === parentType && unit.status === "ACTIVE");
}

type QuantityTierView = SkuView["quantityTiers"][number];

function salesPackageUnit(sku: SkuView): PackageUnit | undefined {
  return sku.packageUnits.find(
    (unit) => unit.unitType === "PACK" && unit.isSalesUnit && unit.status === "ACTIVE",
  )
    ?? sku.packageUnits.find((unit) => unit.unitType === "PACK" && unit.status === "ACTIVE")
    ?? sku.packageUnits.find((unit) => unit.status === "ACTIVE");
}

function inheritedSalesPackageUnit(sku: SkuView): PackageUnit | undefined {
  return sku.packageUnits.find(
    (unit) => unit.unitType === "PACK" && unit.isSalesUnit && unit.status === "ACTIVE",
  )
    // An inactive SKU can still be the only source of the product's historical
    // sales-unit conversion. Prefer active package units, but do not discard
    // that historical configuration when the unit itself was deactivated.
    ?? sku.packageUnits.find((unit) => unit.unitType === "PACK" && unit.isSalesUnit);
}

function materialValue(sku: SkuView): string {
  return sku.attributeValues.find(
    (value) => value.attributeName.includes("材质") || value.attributeCode.toLowerCase().includes("material"),
  )?.displayValue ?? "";
}

function productMaterial(product: ProductDetail): string {
  if (product.material?.trim()) return product.material;
  const sku = product.skus.find((item) => item.status === "ACTIVE" && materialValue(item))
    ?? product.skus.find((item) => materialValue(item));
  return sku ? materialValue(sku) : "";
}

function syncInlinePackageSettings(product: ProductDetail): void {
  const inheritedUnits = product.skus
    .map((sku) => ({ sku, unit: inheritedSalesPackageUnit(sku) }))
    .filter((item): item is { sku: SkuView; unit: PackageUnit } => Boolean(item.unit));
  const first = inheritedUnits[0]?.unit;
  const hasMissingPackage = product.skus.length > 0 && inheritedUnits.length !== product.skus.length;
  const isConsistent = Boolean(first) && !hasMissingPackage && inheritedUnits.every(
    ({ unit }) => unit.unitName === first?.unitName && unit.basePieceCount === first?.basePieceCount,
  );
  inlinePackageSettings.unitName = isConsistent ? first!.unitName : "";
  inlinePackageSettings.basePieceCount = isConsistent ? first!.basePieceCount : null;
  inlinePackageLocked.value = inheritedUnits.length > 0;
  inlinePackageConflict.value = inheritedUnits.length > 0 && !isConsistent;
}

function customConfigNumber(config: unknown, key: "min" | "max" | "step"): number | null {
  if (typeof config !== "object" || config === null || Array.isArray(config)) return null;
  const value = (config as Record<string, unknown>)[key];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function customDimensionFields(config: unknown): CustomOptionDimensionFields {
  if (typeof config !== "object" || config === null || Array.isArray(config)) {
    return { ...DEFAULT_DIMENSION_FIELDS };
  }
  const rawFields = (config as Record<string, unknown>).dimensionFields;
  if (typeof rawFields !== "object" || rawFields === null || Array.isArray(rawFields)) {
    return { ...DEFAULT_DIMENSION_FIELDS };
  }
  const fields = rawFields as Record<string, unknown>;
  return {
    length: typeof fields.length === "boolean" ? fields.length : true,
    width: typeof fields.width === "boolean" ? fields.width : true,
    height: typeof fields.height === "boolean" ? fields.height : true,
    flatLength: typeof fields.flatLength === "boolean" ? fields.flatLength : true,
    flatWidth: typeof fields.flatWidth === "boolean" ? fields.flatWidth : true,
  };
}

function customGroupDraftFrom(group: CustomOptionGroup): CustomGroupDraft {
  return {
    code: group.code,
    name: group.name,
    inputType: group.inputType,
    required: group.required,
    unit: group.unit ?? "",
    min: customConfigNumber(group.config, "min"),
    max: customConfigNumber(group.config, "max"),
    step: customConfigNumber(group.config, "step"),
    sortOrder: group.sortOrder,
    dimensionFields: customDimensionFields(group.config),
  };
}

function emptyCustomValueDraft(): CustomValueDraft {
  return { code: "", name: "", sortOrder: 0 };
}

function syncCustomConfigDrafts(config: AdminCustomizationConfig): void {
  const groupIds = new Set(config.optionGroups.map((group) => group.id));
  for (const groupId of Object.keys(customGroupDrafts)) {
    if (!groupIds.has(groupId)) delete customGroupDrafts[groupId];
  }
  for (const groupId of Object.keys(expandedCustomGroups)) {
    if (!groupIds.has(groupId)) delete expandedCustomGroups[groupId];
  }
  const valueIds = new Set(config.optionGroups.flatMap((group) => group.values.map((value) => value.id)));
  for (const valueId of Object.keys(customValueDrafts)) {
    if (!valueIds.has(valueId)) delete customValueDrafts[valueId];
  }
  for (const group of config.optionGroups) {
    customGroupDrafts[group.id] = customGroupDraftFrom(group);
    for (const value of group.values) {
      customValueDrafts[value.id] = {
        code: value.code,
        name: value.name,
        sortOrder: value.sortOrder,
      };
    }
    if (!newCustomValues[group.id]) newCustomValues[group.id] = emptyCustomValueDraft();
    if (expandedCustomGroups[group.id] === undefined) expandedCustomGroups[group.id] = false;
  }
  for (const groupId of Object.keys(newCustomValues)) {
    if (!groupIds.has(groupId)) delete newCustomValues[groupId];
  }
}

function resetCustomConfigState(): void {
  customConfigRequestVersion += 1;
  customConfig.value = null;
  customConfigLoading.value = false;
  newCustomGroupFormOpen.value = false;
  for (const key of Object.keys(customGroupDrafts)) delete customGroupDrafts[key];
  for (const key of Object.keys(customValueDrafts)) delete customValueDrafts[key];
  for (const key of Object.keys(expandedCustomGroups)) delete expandedCustomGroups[key];
  for (const key of Object.keys(newCustomValues)) delete newCustomValues[key];
  customPresets.value = [];
  customPresetsLoading.value = false;
  selectedCustomPresetIds.value = [];
  Object.assign(newCustomGroup, {
    code: "",
    name: "",
    inputType: "SINGLE_SELECT",
    required: false,
    unit: "",
    min: null,
    max: null,
    step: 1,
    sortOrder: 0,
    dimensionFields: { ...DEFAULT_DIMENSION_FIELDS },
  });
}

async function loadCustomConfiguration(productId: string, throwOnFailure = true): Promise<void> {
  const requestVersion = ++customConfigRequestVersion;
  customConfigLoading.value = true;
  try {
    const config = await getAdminCustomizationConfig(productId);
    if (requestVersion !== customConfigRequestVersion || selected.value?.id !== productId) return;
    customConfig.value = config;
    syncCustomConfigDrafts(config);
    await loadCustomPresets(productId, requestVersion);
  } catch (error) {
    if (requestVersion !== customConfigRequestVersion || selected.value?.id !== productId) return;
    resetCustomConfigState();
    if (throwOnFailure) throw error;
    message.value = `定制参数加载失败：${saveErrorMessage(error)}`;
  } finally {
    if (requestVersion === customConfigRequestVersion) customConfigLoading.value = false;
  }
}

async function loadCustomPresets(productId: string, requestVersion = customConfigRequestVersion): Promise<void> {
  customPresetsLoading.value = true;
  customPresetsError.value = "";
  try {
    const presets = await listCustomOptionPresets();
    if (requestVersion !== customConfigRequestVersion || selected.value?.id !== productId || !selected.value.customizationEnabled) return;
    customPresets.value = presets;
  } catch (error) {
    if (requestVersion === customConfigRequestVersion && selected.value?.id === productId) {
      customPresetsError.value = "公共参数未能完整读取，请重新读取后再编辑";
      message.value = `常用参数加载失败：${saveErrorMessage(error)}`;
    }
  } finally {
    if (requestVersion === customConfigRequestVersion && selected.value?.id === productId) customPresetsLoading.value = false;
  }
}

interface EditableSalesPackageTarget {
  skuId: string;
  packageUnitId: string;
  unitName: string;
}

function editableSalesPackageTargets(product: ProductDetail): EditableSalesPackageTarget[] {
  return product.skus.flatMap((sku) => {
    const unit = sku.packageUnits.find(
      (item) => item.unitType === "PACK" && item.isSalesUnit && item.status === "ACTIVE",
    );
    return unit
      ? [{ skuId: sku.id, packageUnitId: unit.id, unitName: unit.unitName }]
      : [];
  });
}

function saveErrorMessage(error: unknown): string {
  return error instanceof Error && error.message ? error.message : "操作失败";
}

function parameterSaveError(savedParts: string[], failedPart: string, error: unknown): Error {
  const detail = saveErrorMessage(error);
  if (!savedParts.length) return new Error(`${failedPart}保存失败：${detail}`);
  return new Error(
    `商品参数已部分保存：${savedParts.join("、")}已保存；${failedPart}保存失败：${detail}`,
  );
}

function editableSkuAttributes(sku: SkuView): SkuView["attributeValues"] {
  const variantValues = sku.attributeValues.filter((value) => value.isVariantAttribute);
  return variantValues.length > 0 ? variantValues : sku.attributeValues;
}

function activeQuantityTiers(sku: SkuView): SkuView["quantityTiers"] {
  return sku.quantityTiers.filter((tier) => tier.status === "ACTIVE");
}

function priceInputValue(cents: number | null | undefined): string {
  const value = Number(cents);
  return Number.isFinite(value) && value > 0 ? (value / 100).toFixed(2) : "";
}

function priceCentsFromInput(event: Event): number {
  const value = Number((event.target as HTMLInputElement).value);
  return Number.isFinite(value) && value > 0 ? Math.round(value * 100) : 0;
}

function updateTierDraftPrice(sku: SkuView, event: Event): void {
  formFor(sku).tierPrice = priceCentsFromInput(event);
}

function updateTierPrice(tier: QuantityTierView, event: Event): void {
  tier.retailTierPrice = priceCentsFromInput(event);
}

function updateTierPieceQty(tier: QuantityTierView, event: Event): void {
  const value = Number((event.target as HTMLInputElement).value);
  tier.tierPieceQty = Number.isFinite(value) ? value : 0;
}

function statusText(status: string): string {
  return status === "ACTIVE" ? "已启用" : "已停用";
}

async function run(task: () => Promise<void>): Promise<void> {
  busy.value = true;
  message.value = "";
  try {
    await task();
  } catch (error) {
    message.value = error instanceof Error ? error.message : "操作失败";
  } finally {
    busy.value = false;
  }
}

async function refreshProducts(): Promise<void> {
  products.value = await listProducts();
}

async function removeProduct(product: ProductSummary): Promise<void> {
  await run(async () => {
    if (product.status === "ON_SALE") {
      message.value = "商品正在上架，请先下架后再删除。";
      return;
    }
    if (!window.confirm(`确定删除商品「${product.name}」？\n删除后该商品将不再出现在商品列表中。`)) return;

    await archiveProduct(product.id, `Admin 删除商品：${product.name}`);
    products.value = products.value.filter((item) => item.id !== product.id);
    if (selected.value?.id === product.id) {
      if (productSwitcher.value) productSwitcher.value.open = false;
      resetProductForm();
    }
    message.value = "商品已删除";
  });
}

function resetProductForm(): void {
  if (!canLeaveEditors()) return;
  selected.value = null;
  resetCustomConfigState();
  resetDetailSectionDrafts();
  activeSkuId.value = "";
  newSkuDrafts.value = [];
  clearInlineTierDrafts();
  inlinePackageSettings.unitName = "";
  inlinePackageSettings.basePieceCount = null;
  inlinePackageLocked.value = false;
  inlinePackageConflict.value = false;
  Object.assign(productForm, {
    isHot: false,
    productNo: "",
    name: "",
    subtitle: "",
    categoryId: "",
    freightTemplateId: "",
    material: "",
    detailContent: "",
    customizationEnabled: false,
    shippingLeadTime: "",
    shippingDescription: "",
    freeShippingDescription: "",
    guaranteeDescription: "",
    craftsmanshipDescription: "",
    invoiceDescription: "",
    customOrderEnabled: false,
    customizationMoq: null,
    sortOrder: 0,
  });
  activeDetailSectionCode.value = "INTRODUCTION";
  publishResult.value = null;
}

async function selectProduct(productId: string, preserveInlineDrafts = false): Promise<void> {
  const product = await getProduct(productId);
  if (!preserveInlineDrafts && !canLeaveEditors()) return;
  const previousActiveSkuId = activeSkuId.value;
  selected.value = product;
  if (productSwitcher.value) productSwitcher.value.open = false;
  syncInlinePackageSettings(selected.value);
  Object.assign(productForm, {
    productNo: selected.value.productNo,
    isHot: selected.value.isHot,
    name: selected.value.name,
    subtitle: selected.value.subtitle ?? "",
    categoryId: selected.value.categoryId ?? "",
    freightTemplateId: selected.value.freightTemplateId ?? "",
    material: productMaterial(selected.value),
    detailContent: selected.value.detailContent ?? "",
    customizationEnabled: selected.value.customizationEnabled,
    shippingLeadTime: selected.value.shippingLeadTime ?? "",
    shippingDescription: selected.value.shippingDescription ?? "",
    freeShippingDescription: selected.value.freeShippingDescription ?? "",
    guaranteeDescription: selected.value.guaranteeDescription ?? "",
    craftsmanshipDescription: selected.value.craftsmanshipDescription ?? "",
    invoiceDescription: selected.value.invoiceDescription ?? "",
    customOrderEnabled: selected.value.customOrderEnabled,
    customizationMoq: selected.value.customizationMoq,
    sortOrder: selected.value.sortOrder,
  });
  syncDetailSectionDrafts(selected.value);
  if (!preserveInlineDrafts) {
    newSkuDrafts.value = [];
    clearInlineTierDrafts();
  }
  for (const sku of selected.value.skus) formFor(sku);
  activeSkuId.value = selected.value.skus.some((sku) => sku.id === previousActiveSkuId)
    ? previousActiveSkuId
    : selected.value.skus[0]?.id ?? "";
  publishResult.value = null;
  if (selected.value.customizationEnabled) {
    void loadCustomConfiguration(selected.value.id, false);
  } else {
    resetCustomConfigState();
  }
}

async function refreshSelected(): Promise<void> {
  if (!selected.value) return;
  await selectProduct(selected.value.id, true);
  await refreshProducts();
}

async function submitProduct(): Promise<void> {
  if(detailTemplateLocked.value){message.value="请先核对栏目模板操作结果，再保存商品";return;}
  await run(async () => {
    const wasEditing = Boolean(selected.value);
    const savingSectionTemplate=[...detailTemplateTools.values()].some(tool=>Boolean(tool.imported));
    const productNo = productForm.productNo.trim();
    if (!wasEditing && !/^SPU[0-9]{8}$/.test(productNo)) {
      message.value = "SPU 编码格式不正确，应为 SPU + 8 位数字，例如 SPU00000001";
      return;
    }
    const material = productForm.material.trim();
    const materialChanged = !wasEditing && material.length > 0;
    const legacyDetailContent = wasEditing && selected.value?.customizationEnabled
      ? selected.value.detailContent
      : productForm.detailContent.trim() || null;
    const common = {
      isHot: productForm.isHot,
      name: productForm.name.trim(),
      subtitle: productForm.subtitle.trim() || null,
      categoryId: productForm.categoryId || null,
      freightTemplateId: wasEditing
        ? selected!.value!.freightTemplateId
        : resolveCreateFreightTemplateId(productForm.customizationEnabled, productForm.freightTemplateId),
      detailContent: legacyDetailContent,
      customizationEnabled: productForm.customizationEnabled,
      sortOrder: productForm.sortOrder,
    };
    const product = selected.value
      ? await updateProduct(selected.value, common)
      : await createProduct({
        ...common,
        productNo,
        subtitle: common.subtitle ?? undefined,
        detailContent: common.detailContent ?? undefined,
        shippingLeadTime: productForm.shippingLeadTime.trim() || null,
        shippingDescription: productForm.shippingDescription.trim() || null,
        freeShippingDescription: productForm.freeShippingDescription.trim() || null,
        guaranteeDescription: productForm.guaranteeDescription.trim() || null,
        craftsmanshipDescription: productForm.craftsmanshipDescription.trim() || null,
        invoiceDescription: productForm.invoiceDescription.trim() || null,
        customOrderEnabled: productForm.customOrderEnabled,
        customizationMoq: productForm.customOrderEnabled ? productForm.customizationMoq : null,
      });
    if (materialChanged || (!wasEditing && material.length > 0)) {
      await updateProductMaterial(product.id, material);
    }
    if (wasEditing) {
      // Preserve the acknowledged base-product version if the following template transaction rolls back.
      // The template request has its own section revision and unknown-result ledger.
      if(savingSectionTemplate&&selected.value?.id===product.id)selected.value={...selected.value,updatedAt:product.updatedAt};
      await persistAllDetailSections();
    }
    await refreshProducts();
    await selectProduct(product.id);
    if (!product.customizationEnabled) {
      await syncDetailBlocks(product.id);
      if (detailImages.value.some((image) => image.status === "ACTIVE")) {
        await refreshSelected();
      }
    }
    message.value = wasEditing ? `已保存 ${product.productNo}` : `已创建草稿 ${product.productNo}`;
  });
}

async function saveProductParameters(): Promise<void> {
  await run(async () => {
    const product = selected.value;
    if (!product) {
      message.value = "请先保存商品基础信息，再编辑商品参数。";
      return;
    }

    const independentServiceMode = Boolean(product.serviceInfo);
    if (independentServiceMode && !independentServiceEditor.value?.enabled) throw new Error("服务编辑器尚未就绪，请稍后保存");
    const material = productForm.material.trim();
    const materialChanged = material !== productMaterial(product);
    if (materialChanged && !material) {
      throw new Error("材质保存失败：材质不能为空");
    }

    const isCustomProduct = product.customizationEnabled;
    const shippingLeadTime = productForm.shippingLeadTime.trim() || null;
    const shippingDescription = productForm.shippingDescription.trim() || null;
    const freeShippingDescription = productForm.freeShippingDescription.trim() || null;
    const guaranteeDescription = productForm.guaranteeDescription.trim() || null;
    const craftsmanshipDescription = isCustomProduct
      ? product.craftsmanshipDescription ?? null
      : productForm.craftsmanshipDescription.trim() || null;
    const invoiceDescription = productForm.invoiceDescription.trim() || null;
    const customOrderEnabled = isCustomProduct
      ? product.customOrderEnabled
      : productForm.customOrderEnabled;
    const customizationMoq = isCustomProduct
      ? product.customizationMoq
      : customOrderEnabled ? Number(productForm.customizationMoq) : null;
    if (!isCustomProduct && customOrderEnabled) {
      const moq = Number(productForm.customizationMoq);
      if (!Number.isSafeInteger(moq) || moq <= 0) {
        message.value = "支持定制时，请填写有效的定制起订量。";
        return;
      }
    }

    const productParametersChanged = (product.shippingLeadTime ?? "") !== (shippingLeadTime ?? "")
      || (product.shippingDescription ?? "") !== (shippingDescription ?? "")
      || (product.freeShippingDescription ?? "") !== (freeShippingDescription ?? "")
      || (product.guaranteeDescription ?? "") !== (guaranteeDescription ?? "")
      || (product.craftsmanshipDescription ?? "") !== (craftsmanshipDescription ?? "")
      || (product.invoiceDescription ?? "") !== (invoiceDescription ?? "")
      || product.customOrderEnabled !== customOrderEnabled
      || (product.customizationMoq ?? null) !== customizationMoq;
    const freightTemplateId = isCustomProduct
      ? product.freightTemplateId ?? null
      : productForm.freightTemplateId || null;
    const freightChanged = !isCustomProduct && (product.freightTemplateId ?? null) !== freightTemplateId;
    if (freightChanged && product.status === "ON_SALE") {
      throw new Error("运费模板保存失败：上架商品不允许修改运费模板，请先下架后再保存。");
    }

    const packageTargets = editableSalesPackageTargets(product);
    const packageUnitName = inlinePackageSettings.unitName.trim();
    const packageChanged = inlinePackageLocked.value
      && packageTargets.some((target) => target.unitName !== packageUnitName);
    if (packageChanged) {
      if (inlinePackageConflict.value) {
        throw new Error("销售单位保存失败：当前商品已有 SKU 的销售包装不一致，请先统一现有包装配置。");
      }
      if (!packageTargets.length) {
        throw new Error("销售单位保存失败：当前商品没有可编辑的有效销售包装。");
      }
      if (!packageUnitName) {
        throw new Error("销售单位保存失败：请输入有效的销售单位名称。");
      }
    }

    const savedParts: string[] = [];
    let current = product;

    if (independentServiceMode && independentServiceEditor.value?.dirty) {
      try {
        current = await independentServiceEditor.value.save(current);
        selected.value = current;
        savedParts.push("服务信息");
      } catch (error) { throw parameterSaveError(savedParts, "服务信息", error); }
    }
    // Save the service draft against its original CAS before our own attribute update advances it.
    if (materialChanged) {
      try {
        current = await updateProductMaterial(current.id, material);
        selected.value = current;
        savedParts.push("材质");
      } catch (error) {
        throw parameterSaveError(savedParts, "材质", error);
      }
    }
    const legacyParametersChanged = independentServiceMode
      ? product.customOrderEnabled !== customOrderEnabled || (product.customizationMoq ?? null) !== customizationMoq
      : productParametersChanged;
    if (legacyParametersChanged) {
      try {
        const currentValues = independentServiceMode ? {
          shippingLeadTime: current.shippingLeadTime, shippingDescription: current.shippingDescription,
          freeShippingDescription: current.freeShippingDescription, guaranteeDescription: current.guaranteeDescription,
          craftsmanshipDescription: current.craftsmanshipDescription, invoiceDescription: current.invoiceDescription,
        } : {};
        current = await updateProductParameters(current, {
          shippingLeadTime,
          shippingDescription,
          freeShippingDescription,
          guaranteeDescription,
          craftsmanshipDescription,
          invoiceDescription,
          ...currentValues,
          customOrderEnabled,
          customizationMoq,
        });
        selected.value = current;
        savedParts.push("商品参数");
      } catch (error) {
        throw parameterSaveError(savedParts, "商品参数", error);
      }
    }

    if (freightChanged) {
      try {
        current = await updateProduct(current, {
          name: current.name,
          subtitle: current.subtitle,
          categoryId: current.categoryId,
          detailContent: current.detailContent,
          customizationEnabled: current.customizationEnabled,
          freightTemplateId,
          sortOrder: current.sortOrder,
        });
        selected.value = current;
        savedParts.push("运费模板");
      } catch (error) {
        throw parameterSaveError(savedParts, "运费模板", error);
      }
    }

    if (packageChanged) {
      let updatedPackageCount = 0;
      for (const target of packageTargets) {
        if (target.unitName === packageUnitName) continue;
        try {
          const updatedUnit = await updatePackageUnitName(
            current.id,
            target.skuId,
            target.packageUnitId,
            { unitName: packageUnitName },
          );
          updatedPackageCount += 1;
          const targetSku = selected.value?.skus.find((sku) => sku.id === target.skuId);
          const selectedUnit = targetSku?.packageUnits.find((unit) => unit.id === target.packageUnitId);
          if (selectedUnit) selectedUnit.unitName = updatedUnit.unitName;
        } catch (error) {
          const failedPart = updatedPackageCount > 0
            ? `销售单位（已完成 ${updatedPackageCount}/${packageTargets.length} 个 SKU）`
            : "销售单位";
          throw parameterSaveError(savedParts, failedPart, error);
        }
      }
      if (updatedPackageCount > 0) savedParts.push("销售单位");
    }

    if (!savedParts.length) {
      message.value = "商品参数未发生变化";
      return;
    }

    try {
      await refreshSelected();
    } catch (error) {
      throw new Error(`商品参数已保存，但页面刷新失败：${saveErrorMessage(error)}`);
    }
    message.value = "商品参数保存成功";
  });
}

async function unpublish(): Promise<void> {
  if (!selected.value) return;
  await run(async () => {
    await unpublishProduct(selected.value!.id, "Admin 商品维护下架");
    await refreshSelected();
    message.value = "商品已下架";
  });
}

function startInlineSku(): void {
  if (!selected.value) return;
  newSkuDrafts.value.push({
    id: nextInlineSkuDraftId++,
    specName: "",
    tierPieceQty: 50,
    priceYuan: 89,
    error: "",
  });
}

function cancelInlineSku(draftId: number): void {
  if (!busy.value) newSkuDrafts.value = newSkuDrafts.value.filter((draft) => draft.id !== draftId);
}

function clearInlineTierDrafts(): void {
  for (const skuId of Object.keys(inlineTierDrafts)) delete inlineTierDrafts[skuId];
}

function startInlineTier(skuId: string): void {
  if (!inlineTierDrafts[skuId]) {
    inlineTierDrafts[skuId] = {
      tierPieceQty: 100,
      priceYuan: 150,
    };
  }
}

function cancelInlineTier(skuId: string): void {
  if (!busy.value) delete inlineTierDrafts[skuId];
}

async function saveInlineSku(draft: NewSkuDraft): Promise<void> {
  if (!selected.value) return;
  draft.error = "";
  await run(async () => {
    const specName = draft.specName.trim();
    if (!specName) throw new Error("请输入规格名称");
    if (!Number.isSafeInteger(draft.tierPieceQty) || draft.tierPieceQty <= 0) {
      throw new Error("请输入有效的数量（个）");
    }
    if (!Number.isFinite(draft.priceYuan) || draft.priceYuan <= 0) {
      throw new Error("请输入有效的价格（元）");
    }
    const salesUnitName = inlinePackageSettings.unitName.trim();
    const basePieceCount = inlinePackageSettings.basePieceCount;
    if (!salesUnitName) {
      draft.error = "请先填写销售单位";
      message.value = draft.error;
      return;
    }
    if (
      typeof basePieceCount !== "number"
      || !Number.isSafeInteger(basePieceCount)
      || basePieceCount <= 0
    ) {
      draft.error = "请输入有效的每销售单位数量";
      message.value = draft.error;
      return;
    }
    if (draft.tierPieceQty % basePieceCount !== 0) {
      draft.error = "数量必须能被每销售单位数量整除";
      message.value = draft.error;
      return;
    }
    const sku = await createInlineSku(selected.value!.id, {
      specName,
      tierPieceQty: draft.tierPieceQty,
      retailTierPrice: Math.round(draft.priceYuan * 100),
      salesUnitName,
      basePieceCount,
    });
    await refreshSelected();
    newSkuDrafts.value = newSkuDrafts.value.filter((item) => item.id !== draft.id);
    activeSkuId.value = sku.id;
    message.value = `已添加规格 ${specName}`;
  });
}

async function enableSku(skuId: string): Promise<void> {
  if (!selected.value) return;
  await run(async () => {
    await activateSku(selected.value!.id, skuId);
    await refreshSelected();
    message.value = "SKU已启用";
  });
}

async function stopSku(skuId: string): Promise<void> {
  if (!selected.value) return;
  await run(async () => {
    await deactivateSku(selected.value!.id, skuId);
    await refreshSelected();
    message.value = "SKU已停用";
  });
}

async function removeSku(sku: SkuView): Promise<void> {
  if (!selected.value || !window.confirm("确定删除该规格？删除后将停用此 SKU，历史订单仍会保留。")) return;
  await run(async () => {
    await deactivateSku(selected.value!.id, sku.id);
    await refreshSelected();
    delete inlineTierDrafts[sku.id];
    message.value = "规格已删除（SKU 已停用）";
  });
}

async function saveSku(sku: SkuView): Promise<void> {
  if (!selected.value) return;
  await run(async () => {
    const form = formFor(sku);
    await updateSku(selected.value!.id, sku, {
      dimensionNote: form.dimensionNote.trim() || null,
      allowBreakPackage: form.allowBreakPackage,
      retailEnabled: form.retailEnabled,
      sortOrder: form.sortOrder,
    });
    await refreshSelected();
    message.value = "SKU设置已保存";
  });
}

async function saveSkuSpec(sku: SkuView): Promise<void> {
  if (!selected.value) return;
  await run(async () => {
    const values = editableSkuAttributes(sku)
      .map((value) => ({
        attributeDefinitionId: value.attributeDefinitionId,
        optionId: value.optionId,
        displayValue: value.displayValue.trim(),
      }))
      .filter((value) => value.displayValue.length > 0);
    if (values.length === 0) throw new Error("当前SKU没有可编辑规格");
    await updateSku(selected.value!.id, sku, { attributeValues: values });
    await refreshSelected();
    message.value = "规格名称已保存";
  });
}

async function uploadProductMediaFiles(event: Event): Promise<void> {
  const input = event.target as HTMLInputElement;
  const files = Array.from(input.files ?? []);
  input.value = "";
  if (!files.length || !selected.value) return;

  await run(async () => {
    const product = selected.value!;
    const remaining = Math.max(0, 5 - productImages.value.length);
    if (remaining === 0) throw new Error("商品图片已达到 5 张上限");
    if (files.length > remaining) {
      throw new Error(`当前还可添加 ${remaining} 张商品图片，请重新选择不超过 ${remaining} 张`);
    }

    const workingImages = [...productImages.value];
    let uploadedCount = 0;
    const failures: string[] = [];

    for (const [index, file] of files.entries()) {
      message.value = `正在上传第 ${index + 1}/${files.length} 张商品图片…`;
      try {
        const uploaded = await uploadProductImage(file);
        if (!uploaded.url?.trim() || !uploaded.fileId?.trim()) {
          throw new Error("上传成功响应缺少图片地址或文件标识");
        }
        const type = nextImageType(workingImages, false);
        const sortOrder = nextImageSortOrder(workingImages);
        const image = await createImage(product.id, {
          skuId: null,
          type,
          url: uploaded.url.trim(),
          fileId: uploaded.fileId.trim(),
          sortOrder,
        });
        workingImages.push(image);
        uploadedCount += 1;
      } catch (error) {
        failures.push(`${file.name || `第 ${index + 1} 张图片`}：${saveErrorMessage(error)}`);
      }
    }

    await refreshSelected();

    if (failures.length) {
      message.value = `商品图片已添加 ${uploadedCount}/${files.length} 张；失败：${failures.join("；")}`;
      return;
    }
    message.value = `已添加 ${uploadedCount} 张商品图片`;
  });
}

function detailSectionHasUnsavedChanges(code: EditableDetailSectionCode): boolean {
  if (!selected.value) return false;
  if((code==="FILE_SPEC"||code==="CUSTOM_SERVICE")&&detailTemplateTools.get(code)?.imported)return true;
  const section = selected.value.detailSections.find((item) => item.code === code);
  const activeBlocks = (section?.blocks ?? []).filter(isActiveDetailBlock);
  const drafts = detailBlockDrafts[code];
  if (removedDetailBlockIds[code].length || drafts.length !== activeBlocks.length) return true;

  const currentById = new Map(activeBlocks.map((block) => [block.id, block]));
  return drafts.some((draft) => {
    if (!draft.id) {
      return !draft.isLegacyFallback || draft.body.trim() !== draft.legacyBody.trim();
    }
    const current = currentById.get(draft.id);
    return !current
      || current.type !== draft.type
      || (current.title?.trim() ?? "") !== draft.title.trim()
      || (current.body?.trim() ?? "") !== draft.body.trim()
      || (current.image?.id ?? "") !== draft.productImageId
      || current.sortOrder !== draft.sortOrder;
  });
}

async function uploadDetailImageFile(code: EditableDetailSectionCode, file: File): Promise<void> {
  const product = selected.value;
  const definition = editableDetailSections.find((item) => item.code === code)!;
  if (!product) throw new Error("请先保存商品基础信息，再编辑商品详情。");
  const section = product.detailSections.find((item) => item.code === code) ?? null;
  if (section?.status === "INACTIVE") {
    throw new Error(`${definition.label}已停用，请先恢复该栏目后再上传图片`);
  }
  if (detailSectionHasUnsavedChanges(code)) {
    throw new Error(`请先保存${definition.label}当前内容，再上传新图片。`);
  }

  const uploaded = await uploadProductImage(file);
  const image = await createImage(product.id, {
    skuId: null,
    type: "DETAIL",
    url: uploaded.url,
    fileId: uploaded.fileId,
    sortOrder: nextImageSortOrder(detailImages.value),
  });
  let createdSectionId: string | null = null;
  try {
    const sectionId = section?.id ?? (await createDetailSection(product.id, {
      code,
      title: definition.label,
      sortOrder: definition.sortOrder,
    })).id;
    if (!section?.id) createdSectionId = sectionId;
    await createDetailBlock(product.id, sectionId, {
      type: "IMAGE",
      productImageId: image.id,
      sortOrder: nextDetailBlockSortOrder(section?.blocks ?? []),
    });
  } catch (error) {
    try {
      await deactivateImage(product.id, image.id);
    } catch {
      // 保留原始上传关联错误，避免清理失败覆盖真正原因。
    }
    if (createdSectionId) {
      try {
        await deactivateDetailSection(product.id, createdSectionId);
      } catch {
        // 保留原始上传关联错误，避免清理失败覆盖真正原因。
      }
    }
    throw error;
  }
  await refreshSelected();
}

async function uploadDetailImageToSection(code: EditableDetailSectionCode, event: Event): Promise<void> {
  const input = event.target as HTMLInputElement;
  const files = Array.from(input.files ?? []);
  input.value = "";
  if (!files.length) return;

  await run(async () => {
    const definition = editableDetailSections.find((item) => item.code === code)!;
    let uploadedCount = 0;
    const failures: string[] = [];
    for (const [index, file] of files.entries()) {
      message.value = `${definition.label}正在上传第 ${index + 1}/${files.length} 张图片…`;
      try {
        await uploadDetailImageFile(code, file);
        uploadedCount += 1;
      } catch (error) {
        failures.push(`${file.name || `第 ${index + 1} 张图片`}：${saveErrorMessage(error)}`);
      }
    }

    if (failures.length) {
      const result = `${definition.label}已上传 ${uploadedCount}/${files.length} 张图片`;
      message.value = `${result}；失败：${failures.join("；")}`;
      return;
    }
    message.value = `${definition.label}已上传并添加 ${uploadedCount} 张图片`;
  });
}

async function removeImage(imageId: string): Promise<void> {
  if (!selected.value) return;
  await run(async () => {
    await deleteImage(selected.value!.id, imageId);
    await refreshSelected();
    message.value = "商品图片已删除";
  });
}

async function ensurePieceUnit(sku: SkuView): Promise<void> {
  if (!selected.value || sku.packageUnits.some((unit) => unit.unitType === "PIECE" && unit.status === "ACTIVE")) return;
  await createPackageUnit(selected.value.id, sku.id, {
    unitType: "PIECE",
    unitName: "个",
    parentUnitId: null,
    parentUnitCount: null,
    isSalesUnit: false,
  });
}

async function submitPackage(sku: SkuView): Promise<void> {
  if (!selected.value) return;
  await run(async () => {
    await ensurePieceUnit(sku);
    const current = await getProduct(selected.value!.id);
    const refreshedSku = current.skus.find((item) => item.id === sku.id)!;
    const form = formFor(refreshedSku);
    const parents = parentUnits(refreshedSku, form.unitType);
    const parentId = parents.some((unit) => unit.id === form.parentUnitId)
      ? form.parentUnitId
      : parents[0]?.id;
    if (!parentId) throw new Error("请先创建父包装单位");
    await createPackageUnit(selected.value!.id, sku.id, {
      unitType: form.unitType,
      unitName: form.unitType === "PACK" ? "包" : "箱",
      parentUnitId: parentId,
      parentUnitCount: form.parentUnitCount,
      isSalesUnit: form.unitType === "PACK",
    });
    await refreshSelected();
    message.value = "包装单位已保存";
  });
}

async function stopPackage(skuId: string, packageUnitId: string): Promise<void> {
  if (!selected.value) return;
  await run(async () => {
    await deactivatePackageUnit(selected.value!.id, skuId, packageUnitId);
    await refreshSelected();
  });
}

async function submitTier(sku: SkuView, draft?: NewQuantityTierDraft): Promise<boolean> {
  if (!selected.value) return false;
  let saved = false;
  await run(async () => {
    const form = formFor(sku);
    const pack = sku.packageUnits.find(
      (unit) => unit.unitType === "PACK" && unit.isSalesUnit && unit.status === "ACTIVE",
    );
    if (!pack) throw new Error("请先配置有效销售包装“包”");
    await createQuantityTier(selected.value!.id, sku.id, {
      salesPackageUnitId: pack.id,
      tierPieceQty: draft?.tierPieceQty ?? form.tierPieceQty,
      retailTierPrice: draft ? Math.round(draft.priceYuan * 100) : form.tierPrice,
      sortOrder: activeQuantityTiers(sku).length,
    });
    await refreshSelected();
    saved = true;
    message.value = "数量档价格已保存";
  });
  return saved;
}

async function saveInlineTier(sku: SkuView): Promise<void> {
  const draft = inlineTierDrafts[sku.id];
  if (!draft) return;
  if (await submitTier(sku, draft)) delete inlineTierDrafts[sku.id];
}

async function stopTier(skuId: string, tierId: string): Promise<void> {
  if (!selected.value) return;
  await run(async () => {
    await deactivateQuantityTier(selected.value!.id, skuId, tierId);
    await refreshSelected();
  });
}

async function saveTier(skuId: string, tier: SkuView["quantityTiers"][number]): Promise<void> {
  if (!selected.value) return;
  await run(async () => {
    await updateQuantityTier(selected.value!.id, skuId, tier, {
      tierPieceQty: tier.tierPieceQty,
      retailTierPrice: tier.retailTierPrice,
      sortOrder: tier.sortOrder,
    });
    await refreshSelected();
    message.value = "数量档已保存";
  });
}

async function runPublishCheck(): Promise<void> {
  if (!selected.value) return;
  await run(async () => {
    publishResult.value = await checkPublish(selected.value!.id);
    message.value = publishResult.value.passed ? "上架检查通过" : "上架检查未通过，请按原因补齐";
  });
}

async function publish(): Promise<void> {
  if (!selected.value) return;
  await run(async () => {
    await publishProduct(selected.value!.id);
    await refreshSelected();
    message.value = "商品已上架";
  });
}

onMounted(() => run(async () => {
  [products.value, categories.value, freightTemplates.value] = await Promise.all([
    listProducts(),
    listProductCategories(),
    listFreightTemplates(),
  ]);
}));
</script>
<template>
  <section class="product-center product-publish">
    <header class="product-publish__header">
      <div>
        <h1>产品录入</h1>
        <p class="product-publish__intro">
          完成商品信息、媒体、规格与价格后，保存草稿或发布商品。
        </p>
      </div>

      <div class="product-publish__header-actions">
        <details
          ref="productSwitcher"
          class="product-switcher"
        >
          <summary>
            <span>{{ selected ? selected.productNo + " · " + selected.name : "选择已有商品" }}</span>
            <span aria-hidden="true">⌄</span>
          </summary>
          <div class="product-switcher__menu">
            <p class="product-switcher__title">
              商品列表
            </p>
            <div
              v-for="product in products"
              :key="product.id"
              class="product-switcher__item"
              :class="{ 'product-switcher__item--active': selected?.id === product.id }"
            >
              <button
                class="product-switcher__select"
                type="button"
                @click="selectProduct(product.id)"
              >
                <span>
                  <strong>{{ product.productNo }}</strong>
                  <small>{{ product.name }}</small>
                </span>
                <span class="product-switcher__meta">
                  {{ product.customizationEnabled ? "定制" : "现货" }} · {{ product.status }}
                </span>
              </button>
              <button
                class="product-switcher__delete"
                type="button"
                @click.stop="removeProduct(product)"
              >
                删除
              </button>
            </div>
          </div>
        </details>
        <button
          class="button button--secondary"
          :disabled="busy"
          type="button"
          @click="resetProductForm"
        >
          新增商品
        </button>
        <button
          class="button button--ghost"
          :disabled="busy"
          type="button"
          @click="refreshProducts"
        >
          刷新
        </button>
      </div>
    </header>

    <p
      v-if="message"
      class="notice"
    >
      {{ message }}
    </p>

    <main class="product-publish__content">
      <section class="entry-card">
        <div class="entry-card__header">
          <div>
            <h2>基础信息</h2>
          </div>
          <span class="entry-card__hint">商品公共信息</span>
        </div>

        <form
          id="product-form"
          class="field-grid"
          @submit.prevent="submitProduct"
        >
          <label class="field field--wide">
            <span>商品名称 <b>*</b></span>
            <input
              v-model="productForm.name"
              required
              placeholder="请输入商品名称"
            >
          </label>

          <label class="field">
            <span>SPU 编码 <b>*</b></span>
            <input
              v-model="productForm.productNo"
              :disabled="Boolean(selected)"
              required
              placeholder="例如：SPU00000001"
            >
            <small
              v-if="!selected"
              class="field__hint"
            >格式：SPU + 8 位数字</small>
          </label>

          <label class="field">
            <span>副标题</span>
            <input
              v-model="productForm.subtitle"
              placeholder="一句话说明商品特点"
            >
          </label>

          <label class="field">
            <span>一级分类 <b>*</b></span>
            <select
              v-model="selectedRootCategoryId"
              required
            >
              <option
                value=""
                disabled
              >
                请选择一级分类
              </option>
              <option
                v-for="category in rootCategories"
                :key="category.id"
                :value="category.id"
              >
                {{ category.name }}
              </option>
            </select>
          </label>

          <label
            v-if="selectedCategoryChildren.length"
            class="field"
          >
            <span>二级分类 <b>*</b></span>
            <select
              v-model="productForm.categoryId"
              required
            >
              <option
                value=""
                disabled
              >
                请选择二级分类
              </option>
              <option
                v-for="category in selectedCategoryChildren"
                :key="category.id"
                :value="category.id"
              >
                {{ category.name }}
              </option>
            </select>
          </label>

          <div
            v-else-if="selectedRootCategoryId"
            class="field field--legacy"
          >
            <span>分类</span>
            <p>当前为历史分类，保存时保留该分类。</p>
          </div>

          <label class="field">
            <span>商品类型 <b>*</b></span>
            <select
              v-model="productForm.customizationEnabled"
              required
              @change="handleProductTypeChange"
            >
              <option :value="false">现货商品</option>
              <option :value="true">定制商品</option>
            </select>
          </label>

          <label
            v-if="!selected && isCreateFreightRequired(productForm.customizationEnabled)"
            class="field"
          >
            <span>运费模板 <b>*</b></span>
            <select
              v-model="productForm.freightTemplateId"
              required
            >
              <option
                value=""
                disabled
              >
                请选择有效模板
              </option>
              <option
                v-for="template in freightTemplates.filter((item) => item.status === 'ACTIVE')"
                :key="template.id"
                :value="template.id"
              >
                {{ template.name }} · {{ template.ruleType === "FREE" ? "包邮" : "固定 ¥" + (template.fixedAmount / 100).toFixed(2) }}
              </option>
            </select>
          </label>

          <label
            v-if="!selected"
            class="field"
          >
            <span>材质</span>
            <input
              v-model="productForm.material"
              placeholder="例如：牛皮纸"
            >
            <small class="field__hint">商品公共参数，不参与规格选择</small>
          </label>

          <label class="field">
            <span>排序</span>
            <input
              v-model.number="productForm.sortOrder"
              type="number"
              required
            >
          </label>

          <label class="field">
            <span>热门推荐</span>
            <input
              v-model="productForm.isHot"
              class="popular-toggle"
              type="checkbox"
              :disabled="busy"
              aria-label="热门推荐"
            >
            <small class="field__hint">开启后商品可展示在小程序首页“热门推荐”。</small>
          </label>

          <button
            class="sr-only"
            :disabled="busy"
            type="submit"
          >
            保存商品
          </button>
        </form>
      </section>

      <template v-if="selected">
        <StickerPurchaseEditor
          v-if="selected.id === '26'"
          :key="selected.id"
          @policy-saved="value => { productForm.customOrderEnabled = value; productForm.customizationMoq = value ? 500 : null; }"
        />
        <section
          id="product-media"
          class="entry-card media-card"
        >
          <div class="entry-card__header">
            <div>
              <h2>商品媒体库</h2>
            </div>
            <span class="entry-card__hint">{{ productImages.length }}/5 张轮播图片</span>
          </div>

          <div class="media-upload">
            <div class="media-gallery">
              <div class="media-gallery__heading">
                <div>
                  <strong>商品图片</strong>
                  <span>最多 5 张，图片顺序即小程序详情页轮播顺序</span>
                </div>
                <span>{{ productImages.length }}/5</span>
              </div>

              <div class="media-grid media-grid--product">
                <label
                  v-if="productImages.length < 5"
                  class="media-add-tile"
                >
                  <input
                    accept="image/jpeg,image/png,image/webp"
                    type="file"
                    multiple
                    :disabled="busy"
                    @change="uploadProductMediaFiles"
                  >
                  <span class="media-add-tile__plus">＋</span>
                  <strong>添加商品图片</strong>
                  <small>JPG、PNG 或 WebP</small>
                </label>

                <article
                  v-for="(image, index) in productImages"
                  :key="image.id"
                  class="media-tile"
                  :class="{
                    'media-tile--main': image.type === 'MAIN',
                    'media-tile--dragging': draggingProductImageId === image.id,
                    'media-tile--drop-target': productImageDropTargetId === image.id,
                  }"
                  draggable="true"
                  title="拖动图片调整顺序"
                  @mousedown="onProductImagePointerDown(image, $event)"
                  @mouseenter="onProductImagePointerEnter(image)"
                  @mouseup="onProductImagePointerUp(image, $event)"
                  @dragstart="onProductImageDragStart(image, $event)"
                  @dragover="onProductImageDragOver(image, $event)"
                  @drop="onProductImageDrop(image, $event)"
                  @dragend="clearProductImageDrag"
                >
                  <div class="media-tile__image">
                    <img
                      :src="image.url"
                      :alt="image.altText || `商品图片 ${index + 1}`"
                      draggable="false"
                      loading="lazy"
                    >
                    <span
                      v-if="image.type === 'MAIN'"
                      class="media-tile__main-marker"
                    >主图</span>
                    <button
                      class="media-tile__delete"
                      :disabled="busy"
                      type="button"
                      aria-label="删除商品图片"
                      title="删除图片"
                      @mousedown.stop
                      @click.stop="removeImage(image.id)"
                    >
                      ×
                    </button>
                  </div>
                </article>
              </div>
            </div>

            <div class="media-upload__actions">
              <span>可一次选择多张图片；上传后自动加入轮播，首张自动作为商品主图。</span>
            </div>
          </div>
        </section>

        <section
          id="product-parameters"
          class="entry-card packaging-settings product-parameters"
        >
          <div class="entry-card__header">
            <div>
              <h2>{{ selected?.customizationEnabled || selected?.serviceInfo ? "商品服务信息" : "商品参数" }}</h2>
            </div>
            <span class="entry-card__hint">
              {{ selected?.customizationEnabled ? "商品级服务参数" : "商品级参数与当前 SKU 包装配置" }}
            </span>
          </div>
          <ProductServiceInfoEditor
            v-if="selected"
            ref="independentServiceEditor"
            :key="selected.id + '-independent-services'"
            :product="selected"
            :material="productForm.material"
            :disabled="busy"
            @saved="independentServiceSaved"
          />
          <CustomServiceTemplates
            v-if="selected?.customizationEnabled && !selected?.serviceInfo"
            :key="selected.id"
            :product="selected"
            :dirty="serviceFieldsDirty"
            :disabled="busy"
            @saved="serviceBindingSaved"
          />
          <div class="packaging-settings__fields">
            <template v-if="!selected?.customizationEnabled || !hasActiveCustomMaterialGroup">
              <label class="field field--compact">
                <span>材质</span>
                <input
                  v-model="productForm.material"
                  placeholder="例如：牛皮纸"
                >
                <small class="field__hint">商品公共参数，不参与规格选择</small>
              </label>
            </template>
            <label
              v-if="!selected?.serviceInfo"
              class="field field--compact"
            >
              <span>{{ selected?.customizationEnabled ? "生产周期" : "发货时效" }}</span>
              <input
                v-model="productForm.shippingLeadTime"
                :disabled="inheritsServiceField('shippingLeadTime')"
                maxlength="100"
                :placeholder="selected?.customizationEnabled ? '请输入实际确认的生产周期' : '例如：当天发货'"
              >
            </label>
            <template v-if="selected?.customizationEnabled && !selected?.serviceInfo">
              <label
                v-if="!selected?.serviceInfo"
                class="field field--compact"
              >
                <span>生产与发货说明</span>
                <textarea
                  v-model="productForm.shippingDescription"
                  :disabled="inheritsServiceField('shippingDescription')"
                  maxlength="500"
                  rows="2"
                  placeholder="请输入当前商品的生产与发货说明"
                />
              </label>
              <label
                v-if="!selected?.serviceInfo"
                class="field field--compact"
              >
                <span>定制运费说明</span>
                <textarea
                  v-model="productForm.freeShippingDescription"
                  :disabled="inheritsServiceField('freeShippingDescription')"
                  maxlength="500"
                  rows="2"
                  placeholder="请输入当前商品的运输方式及运费说明"
                />
              </label>
              <label
                v-if="!selected?.serviceInfo"
                class="field field--compact"
              >
                <span>保障说明</span>
                <textarea
                  v-model="productForm.guaranteeDescription"
                  :disabled="inheritsServiceField('guaranteeDescription')"
                  maxlength="500"
                  rows="2"
                  placeholder="请输入商品保障说明"
                />
              </label>
              <label
                v-if="!selected?.serviceInfo"
                class="field field--compact"
              >
                <span>发票说明</span>
                <textarea
                  v-model="productForm.invoiceDescription"
                  :disabled="inheritsServiceField('invoiceDescription')"
                  maxlength="500"
                  rows="2"
                  placeholder="请输入实际发票说明"
                />
              </label>
            </template>
            <template v-if="!selected?.customizationEnabled">
              <label
                v-if="!selected?.serviceInfo"
                class="field field--compact"
              >
                <span>发货说明</span>
                <textarea
                  v-model="productForm.shippingDescription"
                  maxlength="500"
                  rows="2"
                  placeholder="请输入现货发货说明"
                />
              </label>
              <label
                v-if="!selected?.serviceInfo"
                class="field field--compact"
              >
                <span>包邮说明</span>
                <textarea
                  v-model="productForm.freeShippingDescription"
                  maxlength="500"
                  rows="2"
                  placeholder="请输入包邮/运费补充说明"
                />
              </label>
              <label
                v-if="!selected?.serviceInfo"
                class="field field--compact"
              >
                <span>保障说明</span>
                <textarea
                  v-model="productForm.guaranteeDescription"
                  maxlength="500"
                  rows="2"
                  placeholder="请输入售后保障说明"
                />
              </label>
              <label
                v-if="!selected?.serviceInfo"
                class="field field--compact"
              >
                <span>发票说明</span>
                <textarea
                  v-model="productForm.invoiceDescription"
                  maxlength="500"
                  rows="2"
                  placeholder="请输入实际发票说明"
                />
              </label>
              <label class="field field--compact">
                <span>支持定制</span>
                <select v-model="productForm.customOrderEnabled">
                  <option :value="false">否</option>
                  <option :value="true">是</option>
                </select>
              </label>
              <label class="field field--compact">
                <span>定制起订量</span>
                <div class="packaging-settings__count-input">
                  <input
                    v-model.number="productForm.customizationMoq"
                    :disabled="!productForm.customOrderEnabled || busy"
                    min="1"
                    step="1"
                    type="number"
                    placeholder="例如：100"
                  >
                  <span>个</span>
                </div>
              </label>
            </template>
            <template v-if="!selected?.customizationEnabled">
              <label class="field field--compact">
                <span>基础单位</span>
                <input
                  :value="baseUnitName"
                  disabled
                  readonly
                >
                <small class="field__hint">来自当前 SKU 的 PIECE 包装单位</small>
              </label>
            </template>
            <label
              v-if="!selected?.customizationEnabled"
              class="field field--compact"
            >
              <span>运费模板</span>
              <select
                v-model="productForm.freightTemplateId"
                :disabled="busy || selected?.status === 'ON_SALE'"
              >
                <option
                  value=""
                  disabled
                >
                  请选择有效模板
                </option>
                <option
                  v-for="template in freightTemplates.filter((item) => item.status === 'ACTIVE')"
                  :key="template.id"
                  :value="template.id"
                >
                  {{ template.name }} · {{ template.ruleType === "FREE" ? "包邮" : "固定 ¥" + (template.fixedAmount / 100).toFixed(2) }}
                </option>
              </select>
              <small
                v-if="selected?.status === 'ON_SALE'"
                class="field__hint"
              >上架商品不允许修改运费模板，请先下架后再保存。</small>
            </label>
            <template v-if="!selected?.customizationEnabled && (inlinePackageLocked || (selected?.skus.length ?? 0) > 0)">
              <label class="field field--compact">
                <span>销售单位</span>
                <input
                  v-model="inlinePackageSettings.unitName"
                  :disabled="inlinePackageLocked || busy"
                  maxlength="20"
                  placeholder="例如：包"
                >
              </label>
              <label class="field field--compact packaging-settings__count-field">
                <span>每销售单位数量</span>
                <div class="packaging-settings__count-input">
                  <input
                    v-model.number="inlinePackageSettings.basePieceCount"
                    :disabled="inlinePackageLocked || busy"
                    min="1"
                    step="1"
                    type="number"
                    placeholder="例如：10"
                  >
                  <span>个</span>
                </div>
              </label>
            </template>
            <button
              class="button button--secondary product-parameters__save"
              :disabled="busy"
              type="button"
              @click="saveProductParameters"
            >
              保存服务信息
            </button>
          </div>
          <p
            v-if="!selected?.customizationEnabled && inlinePackageConflict"
            class="packaging-settings__message packaging-settings__message--error"
          >
            当前商品已有 SKU 的销售包装不一致，暂不能直接新增规格，请先统一现有包装配置。
          </p>
          <p
            v-else-if="!selected?.customizationEnabled && inlinePackageLocked"
            class="packaging-settings__message"
          >
            新增规格将沿用当前商品已有的 {{ inlinePackageSettings.unitName }} × {{ inlinePackageSettings.basePieceCount }} 个换算。材质、运费模板和其他服务信息均通过上方“保存服务信息”保存。
          </p>
          <p
            v-else-if="!selected?.customizationEnabled && (selected?.skus.length ?? 0) > 0"
            class="packaging-settings__message"
          >
            没有现有包装配置时，请先填写销售单位和换算数量；系统不会替你默认数量。
          </p>
        </section>

        <section
          v-if="selected?.customizationEnabled"
          id="custom-option-config"
          class="entry-card custom-config"
        >
          <BoundCustomParameterEditor
            ref="customParameterEditor"
            :key="selected.id"
            :product-id="selected.id"
            :config="customConfig"
            :presets="customPresets"
            :load-error="customPresetsError"
            :loading="customConfigLoading || customPresetsLoading"
            :disabled="busy"
            @saved="customParametersSaved"
            @reload="loadCustomConfiguration(selected.id)"
          />
        </section>

        <section
          v-if="!selected?.customizationEnabled"
          id="product-specs"
          class="entry-card specs-card"
        >
          <div class="entry-card__header">
            <div class="specs-card__title">
              <h2>规格与价格</h2>
            </div>
            <span class="entry-card__hint">{{ activeSkus.length }} 个有效 SKU</span>
          </div>

          <div class="sku-groups">
            <section
              v-for="sku in activeSkus"
              :key="sku.id"
              class="sku-group"
            >
              <div class="sku-group__header">
                <div class="sku-group__identity">
                  <span class="sku-group__label">规格名称</span>
                  <div class="sku-group__name">
                    <template v-if="editableSkuAttributes(sku).length">
                      <label
                        v-for="value in editableSkuAttributes(sku)"
                        :key="value.attributeDefinitionId"
                        class="sku-group__spec-input"
                      >
                        <span class="sr-only">规格名称</span>
                        <input
                          v-model="value.displayValue"
                          type="text"
                          @blur="saveSkuSpec(sku)"
                        >
                      </label>
                    </template>
                    <strong v-else>{{ Object.values(sku.specSummary).join(" / ") || "默认规格" }}</strong>
                    <small>SKU编号 {{ sku.skuNo }}</small>
                  </div>
                </div>
                <button
                  class="sku-group__delete"
                  :disabled="busy"
                  type="button"
                  @click="removeSku(sku)"
                >
                  删除规格
                </button>
              </div>

              <div class="sku-tier-header">
                <span>数量</span>
                <span>价格（元）</span>
                <span>操作</span>
              </div>
              <div
                v-for="tier in activeQuantityTiers(sku)"
                :key="tier.id"
                class="sku-tier-row"
              >
                <label class="sku-tier-quantity">
                  <span class="sr-only">数量（个）</span>
                  <input
                    :value="tier.tierPieceQty"
                    min="1"
                    step="1"
                    type="number"
                    @input="updateTierPieceQty(tier, $event)"
                  >
                  <span>个</span>
                </label>
                <label class="sku-tier-price">
                  <span class="sr-only">价格（元）</span>
                  <span aria-hidden="true">¥</span>
                  <input
                    :value="priceInputValue(tier.retailTierPrice)"
                    inputmode="decimal"
                    min="0.01"
                    step="0.01"
                    type="number"
                    @input="updateTierPrice(tier, $event)"
                  >
                </label>
                <div class="sku-tier-actions">
                  <button
                    class="button button--ghost button--small"
                    :disabled="busy"
                    type="button"
                    @click="saveTier(sku.id, tier)"
                  >
                    保存
                  </button>
                  <button
                    class="button button--danger button--small"
                    :disabled="busy"
                    type="button"
                    @click="stopTier(sku.id, tier.id)"
                  >
                    删除
                  </button>
                </div>
              </div>
              <p
                v-if="!activeQuantityTiers(sku).length"
                class="media-empty"
              >
                暂无有效数量价格档。
              </p>

              <form
                v-if="inlineTierDrafts[sku.id]"
                class="sku-tier-row sku-tier-row--draft"
                @submit.prevent="saveInlineTier(sku)"
              >
                <label class="sku-tier-quantity">
                  <span class="sr-only">新增数量（个）</span>
                  <input
                    v-model.number="inlineTierDrafts[sku.id].tierPieceQty"
                    min="1"
                    step="1"
                    type="number"
                    required
                  >
                  <span>个</span>
                </label>
                <label class="sku-tier-price">
                  <span class="sr-only">新增价格（元）</span>
                  <span aria-hidden="true">¥</span>
                  <input
                    v-model.number="inlineTierDrafts[sku.id].priceYuan"
                    inputmode="decimal"
                    min="0.01"
                    step="0.01"
                    type="number"
                    required
                  >
                </label>
                <div class="sku-tier-actions">
                  <button
                    class="button button--secondary button--small"
                    :disabled="busy"
                    type="submit"
                  >
                    保存
                  </button>
                  <button
                    class="button button--ghost button--small"
                    :disabled="busy"
                    type="button"
                    @click="cancelInlineTier(sku.id)"
                  >
                    删除
                  </button>
                </div>
              </form>
              <button
                v-else
                class="sku-tier-add"
                :disabled="busy"
                type="button"
                @click="startInlineTier(sku.id)"
              >
                ＋ 添加数量价格
              </button>
            </section>

            <section
              v-for="draft in newSkuDrafts"
              :key="draft.id"
              class="sku-group sku-group--draft"
            >
              <div class="sku-group__header">
                <div class="sku-group__identity">
                  <span class="sku-group__label">规格名称</span>
                  <label class="sku-group__draft-name">
                    <span class="sr-only">新规格名称</span>
                    <input
                      v-model="draft.specName"
                      placeholder="请输入规格名称"
                      type="text"
                    >
                  </label>
                </div>
                <span class="sku-group__draft-status">新规格</span>
              </div>
              <div class="sku-tier-header">
                <span>数量</span>
                <span>价格（元）</span>
                <span>操作</span>
              </div>
              <form
                class="sku-tier-row sku-tier-row--draft"
                @submit.prevent="saveInlineSku(draft)"
              >
                <label class="sku-tier-quantity">
                  <span class="sr-only">数量（个）</span>
                  <input
                    v-model.number="draft.tierPieceQty"
                    min="1"
                    step="1"
                    type="number"
                    required
                  >
                  <span>个</span>
                </label>
                <label class="sku-tier-price">
                  <span class="sr-only">价格（元）</span>
                  <span aria-hidden="true">¥</span>
                  <input
                    v-model.number="draft.priceYuan"
                    inputmode="decimal"
                    min="0.01"
                    step="0.01"
                    type="number"
                    required
                  >
                </label>
                <div
                  v-if="!inlinePackageLocked"
                  class="sku-draft-package-settings"
                >
                  <label class="field field--compact">
                    <span>销售单位</span>
                    <input
                      v-model="inlinePackageSettings.unitName"
                      :disabled="busy"
                      maxlength="20"
                      placeholder="例如：包"
                      required
                    >
                  </label>
                  <label class="field field--compact packaging-settings__count-field">
                    <span>每销售单位数量</span>
                    <div class="packaging-settings__count-input">
                      <input
                        v-model.number="inlinePackageSettings.basePieceCount"
                        :disabled="busy"
                        min="1"
                        step="1"
                        type="number"
                        placeholder="例如：10"
                        required
                      >
                      <span>个</span>
                    </div>
                  </label>
                  <small class="field__hint">数量必须能被每销售单位数量整除。</small>
                </div>
                <p
                  v-else
                  class="field__hint sku-draft-package-settings__locked"
                >
                  将沿用当前商品已有的 {{ inlinePackageSettings.unitName }} × {{ inlinePackageSettings.basePieceCount }} 个销售包装换算。
                </p>
                <p
                  v-if="draft.error"
                  class="sku-draft-error"
                  role="alert"
                >
                  {{ draft.error }}
                </p>
                <div class="sku-tier-actions">
                  <button
                    class="button button--secondary button--small"
                    :disabled="busy"
                    type="submit"
                  >
                    保存规格
                  </button>
                  <button
                    class="button button--ghost button--small"
                    :disabled="busy"
                    type="button"
                    @click="cancelInlineSku(draft.id)"
                  >
                    删除
                  </button>
                </div>
              </form>
            </section>

            <p
              v-if="!activeSkus.length && !newSkuDrafts.length"
              class="media-empty"
            >
              暂无有效 SKU，请先添加规格。
            </p>
          </div>

          <button
            class="spec-add-inline"
            :disabled="busy"
            type="button"
            @click="startInlineSku"
          >
            ＋ 添加规格
          </button>

          <details
            v-if="activeSku"
            class="sku-editor-details"
            :open="Boolean(activeSkuId)"
          >
            <summary class="sku-editor-details__summary">
              <div>
                <strong>编辑规格</strong>
                <span>{{ Object.values(activeSku.specSummary).join(" / ") || "默认规格" }} · {{ activeSku.skuNo }}</span>
              </div>
              <span>收起</span>
            </summary>

            <section class="sku-editor">
              <div class="sku-editor__header">
                <div>
                  <h3>{{ Object.values(activeSku.specSummary).join(" / ") || "默认规格" }}</h3>
                  <span>SKU 编号 {{ activeSku.skuNo }} · {{ statusText(activeSku.status) }}</span>
                </div>
                <button
                  v-if="activeSku.status === 'INACTIVE'"
                  class="button button--secondary"
                  type="button"
                  @click="enableSku(activeSku.id)"
                >
                  启用规格
                </button>
                <button
                  v-else
                  class="button button--ghost"
                  type="button"
                  @click="stopSku(activeSku.id)"
                >
                  停用规格
                </button>
              </div>

              <div class="sku-daily-editor">
                <div class="sku-daily-editor__row">
                  <span>SKU / 规格</span>
                  <strong>{{ Object.values(activeSku.specSummary).join(" / ") || "默认规格" }}</strong>
                  <small>{{ activeSku.skuNo }}</small>
                </div>
                <div class="sku-daily-editor__row">
                  <span>包装</span>
                  <strong>{{ salesPackageUnit(activeSku)?.conversionText || "未配置包装单位" }}</strong>
                </div>
              </div>

              <div class="tier-compact-editor">
                <div class="editor-block__heading">
                  <strong>阶梯价格</strong>
                  <span>录入起订量与对应单价</span>
                </div>
                <div
                  v-for="tier in activeSku.quantityTiers"
                  :key="tier.id"
                  class="tier-compact-row"
                >
                  <span>{{ tier.label }} / {{ tier.packageLabel }}</span>
                  <label class="price-input">
                    <span>¥</span>
                    <span class="sr-only">单价（元）</span>
                    <input
                      :value="priceInputValue(tier.retailTierPrice)"
                      inputmode="decimal"
                      min="0.01"
                      step="0.01"
                      type="number"
                      @input="updateTierPrice(tier, $event)"
                    >
                  </label>
                  <span
                    v-if="tier.status !== 'ACTIVE'"
                    class="tier-compact-row__status"
                  >
                    {{ statusText(tier.status) }}
                  </span>
                  <form @submit.prevent="saveTier(activeSku.id, tier)">
                    <button
                      class="button button--ghost button--small"
                      type="submit"
                    >
                      保存
                    </button>
                  </form>
                  <button
                    v-if="tier.status === 'ACTIVE'"
                    class="button button--danger button--small"
                    type="button"
                    @click="stopTier(activeSku.id, tier.id)"
                  >
                    停用
                  </button>
                </div>
                <p
                  v-if="!activeSku.quantityTiers.length"
                  class="media-empty"
                >
                  暂无价格档，请先添加。
                </p>

                <details class="tier-add">
                  <summary>+ 添加价格档</summary>
                  <form
                    class="tier-add__form"
                    @submit.prevent="submitTier(activeSku)"
                  >
                    <label class="field field--compact field--number">
                      <span>起订量（个）</span>
                      <input
                        v-model.number="formFor(activeSku).tierPieceQty"
                        min="1"
                        type="number"
                        required
                      >
                    </label>
                    <label class="field field--compact field--number">
                      <span>价格（元）</span>
                      <input
                        :value="priceInputValue(formFor(activeSku).tierPrice)"
                        inputmode="decimal"
                        min="0.01"
                        step="0.01"
                        type="number"
                        required
                        @input="updateTierDraftPrice(activeSku, $event)"
                      >
                    </label>
                    <button
                      class="button button--secondary"
                      type="submit"
                    >
                      添加价格档
                    </button>
                  </form>
                </details>
              </div>

              <details class="sku-editor__advanced">
                <summary>更多设置</summary>
                <div class="sku-editor__advanced-body">
                  <form
                    class="editor-block"
                    @submit.prevent="saveSku(activeSku)"
                  >
                    <div class="editor-block__heading">
                      <strong>规格设置</strong>
                      <span>仅在需要时调整</span>
                    </div>
                    <div class="field-grid field-grid--compact">
                      <label class="field">
                        <span>尺寸备注</span>
                        <input
                          v-model="formFor(activeSku).dimensionNote"
                          placeholder="可选"
                        >
                      </label>
                      <label class="field field--number">
                        <span>显示排序</span>
                        <input
                          v-model.number="formFor(activeSku).sortOrder"
                          type="number"
                        >
                      </label>
                      <label class="check-field">
                        <input
                          v-model="formFor(activeSku).retailEnabled"
                          type="checkbox"
                        >
                        <span>允许零售</span>
                      </label>
                      <label class="check-field">
                        <input
                          v-model="formFor(activeSku).allowBreakPackage"
                          type="checkbox"
                        >
                        <span>允许拆包</span>
                      </label>
                    </div>
                    <button
                      class="button button--secondary"
                      :disabled="busy"
                      type="submit"
                    >
                      保存规格设置
                    </button>
                  </form>

                  <div class="editor-block">
                    <div class="editor-block__heading">
                      <strong>包装单位设置</strong>
                      <span>调整换算关系时使用</span>
                    </div>
                    <form
                      class="field-grid field-grid--compact"
                      @submit.prevent="submitPackage(activeSku)"
                    >
                      <label class="field">
                        <span>新增单位</span>
                        <select v-model="formFor(activeSku).unitType">
                          <option value="PACK">包</option>
                          <option value="BOX">箱</option>
                        </select>
                      </label>
                      <label class="field">
                        <span>父单位</span>
                        <select v-model="formFor(activeSku).parentUnitId">
                          <option
                            v-for="unit in parentUnits(activeSku, formFor(activeSku).unitType)"
                            :key="unit.id"
                            :value="unit.id"
                          >
                            {{ unit.conversionText }}
                          </option>
                        </select>
                      </label>
                      <label class="field field--number">
                        <span>父单位数量</span>
                        <input
                          v-model.number="formFor(activeSku).parentUnitCount"
                          min="1"
                          type="number"
                          required
                        >
                      </label>
                      <button
                        class="button button--secondary"
                        type="submit"
                      >
                        保存包装单位
                      </button>
                    </form>
                    <div class="unit-list">
                      <div
                        v-for="unit in activeSku.packageUnits"
                        :key="unit.id"
                        class="unit-chip"
                      >
                        <span>{{ unit.conversionText }} · {{ statusText(unit.status) }}</span>
                        <button
                          v-if="unit.status === 'ACTIVE'"
                          class="button button--ghost button--small"
                          type="button"
                          @click="stopPackage(activeSku.id, unit.id)"
                        >
                          停用
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </details>
            </section>
          </details>
        </section>

        <section
          id="product-detail"
          class="entry-card detail-card"
        >
          <div class="entry-card__header detail-card__header">
            <div>
              <h2>{{ selected?.customizationEnabled ? "详情介绍" : "商品详情" }}</h2>
            </div>
            <div
              v-if="selected"
              class="detail-section-tabs"
              role="tablist"
              aria-label="商品详情栏目"
            >
              <button
                v-for="detailSection in visibleEditableDetailSections"
                :key="detailSection.code"
                class="detail-section-tabs__tab"
                :class="{ 'detail-section-tabs__tab--active': activeDetailSectionCode === detailSection.code }"
                :aria-selected="activeDetailSectionCode === detailSection.code"
                role="tab"
                type="button"
                @click="activeDetailSectionCode = detailSection.code"
              >
                {{ detailSection.label }}
              </button>
            </div>
          </div>

          <div class="detail-card__content">
            <label
              v-if="!selected?.customizationEnabled"
              class="field field--wide"
            >
              <span>商品介绍</span>
              <small class="field__hint">
                内容继续按现有 detailContent HTML 格式保存。
              </small>
              <textarea
                v-model="productForm.detailContent"
                class="field__code"
                rows="5"
                placeholder="例如：<p>材质与包装说明</p>"
              />
            </label>

            <div v-if="selected">
              <DetailSectionTemplateTools
                v-for="code in templateSectionCodes"
                :key="`${selected.id}:${code}`"
                :ref="value=>registerDetailTemplateTool(code,value)"
                :product-id="selected.id"
                :code="code"
                :snapshot="detailTemplateDraft(code)"
                :current-content="sectionContent(selected.detailSections.find(section=>section.code===code),code)"
                :capture-undo="()=>captureDetailTemplateUndo(code)"
                :disabled="busy||detailTemplateLocked||detailSectionDrafts[code].status==='INACTIVE'"
                :readback="readbackDetailTemplate"
                @apply="value=>importDetailTemplate(code,value)"
                @lock="value=>detailTemplateLocks[code]=value"
              />
            </div>
            <div
              v-if="selected"
              class="detail-sections-editor"
              :inert="detailTemplateLocked"
            >
              <article class="detail-section-editor">
                <div class="detail-block-editor__toolbar">
                  <div class="inline-actions">
                    <button
                      class="button button--ghost button--small"
                      :disabled="busy || detailSectionDrafts[activeDetailSection.code].status === 'INACTIVE'"
                      type="button"
                      @click="addDetailBlock(activeDetailSection.code, 'TEXT')"
                    >
                      ＋ 添加文字
                    </button>
                    <label
                      class="button button--ghost button--small detail-block-editor__upload"
                      :aria-disabled="busy || detailSectionDrafts[activeDetailSection.code].status === 'INACTIVE'"
                    >
                      ＋ 添加图片
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        multiple
                        :disabled="busy || detailSectionDrafts[activeDetailSection.code].status === 'INACTIVE'"
                        @change="uploadDetailImageToSection(activeDetailSection.code, $event)"
                      >
                    </label>
                  </div>
                  <button
                    v-if="detailSectionDrafts[activeDetailSection.code].sectionId && detailSectionDrafts[activeDetailSection.code].status !== 'INACTIVE'"
                    class="button button--ghost button--small"
                    :disabled="busy"
                    type="button"
                    @click="clearDetailSection(activeDetailSection.code)"
                  >
                    清空
                  </button>
                </div>

                <div class="detail-block-editor">
                  <article
                    v-for="(block, blockIndex) in detailBlockDrafts[activeDetailSection.code]"
                    :key="block.clientKey"
                    class="detail-block-draft"
                    :class="{
                      'detail-block-draft--image': isDetailImageBlock(block),
                      'detail-block-draft--image-dragging': draggingDetailImage?.code === activeDetailSection.code
                        && draggingDetailImage.clientKey === block.clientKey,
                      'detail-block-draft--image-drop-target': detailImageDropTarget?.code === activeDetailSection.code
                        && detailImageDropTarget.clientKey === block.clientKey,
                    }"
                    :draggable="isDetailImageBlock(block)"
                    :title="isDetailImageBlock(block) ? '拖动图片调整顺序' : undefined"
                    @mousedown="onDetailImagePointerDown(activeDetailSection.code, block, $event)"
                    @mouseenter="onDetailImagePointerEnter(activeDetailSection.code, block)"
                    @mouseup="onDetailImagePointerUp(activeDetailSection.code, block, $event)"
                    @dragstart="onDetailImageDragStart(activeDetailSection.code, block, $event)"
                    @dragover="onDetailImageDragOver(activeDetailSection.code, block, $event)"
                    @drop="onDetailImageDrop(activeDetailSection.code, block, $event)"
                    @dragend="clearDetailImageDrag"
                  >
                    <div
                      v-if="block.type === 'TEXT' || block.type === 'IMAGE_TEXT'"
                      class="detail-block-draft__summary"
                    >
                      <div class="detail-block-draft__summary-content">
                        <strong v-if="block.title">{{ block.title }}</strong>
                        <p v-if="!isDetailBlockEditing(activeDetailSection.code, block)">
                          {{ detailBlockSummary(block) }}
                        </p>
                      </div>
                    </div>

                    <div
                      v-if="block.type === 'IMAGE' || block.type === 'IMAGE_TEXT'"
                      class="detail-block-draft__media"
                    >
                      <div
                        v-if="detailDraftImage(block)"
                        class="detail-block-draft__preview"
                      >
                        <img
                          :src="detailDraftImage(block)?.url"
                          :alt="detailDraftImage(block)?.altText || '详情图片预览'"
                          draggable="false"
                        >
                      </div>
                      <div
                        v-else
                        class="detail-block-draft__preview detail-block-draft__preview--empty"
                      >
                        尚未选择图片
                      </div>
                    </div>

                    <div
                      v-if="(block.type === 'TEXT' || block.type === 'IMAGE_TEXT') && isDetailBlockEditing(activeDetailSection.code, block)"
                      class="detail-block-draft__fields"
                    >
                      <label class="field field--wide">
                        <span>标题（可选）</span>
                        <input
                          v-model="block.title"
                          :disabled="busy"
                          placeholder="例如：文件格式"
                        >
                      </label>
                      <label class="field field--wide">
                        <span>文字内容</span>
                        <textarea
                          v-model="block.body"
                          :disabled="busy"
                          rows="4"
                          placeholder="请输入这一段说明"
                        />
                      </label>
                    </div>

                    <div class="detail-block-draft__actions">
                      <button
                        v-if="block.type === 'TEXT' || block.type === 'IMAGE_TEXT'"
                        class="button button--ghost button--small"
                        :disabled="busy"
                        type="button"
                        @click="toggleDetailBlockEditing(activeDetailSection.code, block)"
                      >
                        {{ isDetailBlockEditing(activeDetailSection.code, block) ? "收起" : "编辑" }}
                      </button>
                      <button
                        v-if="block.type === 'TEXT' || block.type === 'IMAGE_TEXT'"
                        class="button button--ghost button--small detail-block-draft__remove"
                        :aria-label="`删除第 ${blockIndex + 1} 个文字块`"
                        :disabled="busy"
                        type="button"
                        @mousedown.stop
                        @click="removeDetailBlock(activeDetailSection.code, blockIndex)"
                      >
                        删除
                      </button>
                      <button
                        v-else
                        class="detail-block-draft__remove-image"
                        :aria-label="`删除第 ${blockIndex + 1} 张详情图片`"
                        title="删除图片"
                        :disabled="busy"
                        type="button"
                        @mousedown.stop
                        @click="removeDetailBlock(activeDetailSection.code, blockIndex)"
                      >
                        ×
                      </button>
                    </div>
                  </article>
                </div>
              </article>
            </div>
          </div>
        </section>

        <section
          id="publish-check"
          class="entry-card publish-check"
        >
          <div class="entry-card__header">
            <div>
              <h2>发布检查</h2>
            </div>
            <span
              v-if="publishResult"
              class="check-status"
              :class="{ 'check-status--passed': publishResult.passed }"
            >
              {{ publishResult.passed ? "检查通过" : "需要处理" }}
            </span>
            <span
              v-else
              class="check-status check-status--pending"
            >
              尚未检查
            </span>
          </div>

          <p
            v-if="!publishResult"
            class="publish-check__empty"
          >
            完成基础信息、图片、SKU、包装单位和数量档后执行检查。
          </p>
          <div
            v-else
            class="publish-check__groups"
          >
            <details
              v-for="group in publishResult.groups"
              :key="group.group"
              class="check-group"
              :class="{ 'check-group--passed': group.status === 'PASS' }"
            >
              <summary>
                <span>{{ group.group }}</span>
                <strong>{{ group.status }}</strong>
              </summary>
              <ul>
                <li
                  v-for="item in group.items"
                  :key="item.code"
                >
                  {{ item.message }}
                </li>
              </ul>
            </details>
          </div>
        </section>
      </template>
    </main>
    <footer class="publish-footer">
      <div class="publish-footer__context">
        <span>{{ selected ? selected.productNo + " · " + selected.status : "新建商品草稿" }}</span>
        <strong>{{ selected ? selected.name : "填写基础信息后保存" }}</strong>
      </div>
      <div class="publish-footer__actions">
        <button
          class="button button--secondary"
          form="product-form"
          :disabled="busy"
          type="submit"
        >
          {{ selected ? "保存商品" : "创建草稿" }}
        </button>
        <button
          v-if="selected"
          class="button button--ghost"
          :disabled="busy"
          type="button"
          @click="runPublishCheck"
        >
          执行检查
        </button>
        <button
          v-if="selected"
          class="button button--primary"
          :disabled="busy || !publishResult?.passed"
          type="button"
          @click="publish"
        >
          发布商品
        </button>
        <button
          v-if="selected?.status === 'ON_SALE'"
          class="button button--danger"
          :disabled="busy"
          type="button"
          @click="unpublish"
        >
          下架商品
        </button>
      </div>
    </footer>
  </section>
</template>
<style scoped>
#product-parameters {
  --publish-ink: #171717;
  --publish-brand: #171717;
  --publish-brand-deep: #171717;
  --publish-muted: #666;
  --publish-line: #e5e5e5;
  --publish-danger: #444;
}
#product-parameters .field input,
#product-parameters .field select,
#product-parameters .field textarea {
  border-color: #d4d4d4;
  background: #fff;
  color: #171717;
}
#product-parameters .field input::placeholder,
#product-parameters .field textarea::placeholder {
  color: #737373;
}
#product-parameters .product-parameters__save {
  border-color: #171717;
  background: #171717;
  color: #fff;
}
#product-parameters .product-parameters__save:hover:not(:disabled) {
  border-color: #333;
  background: #333;
}
.product-publish {
  --publish-ink: var(--color-text, #1b1d1f);
  --publish-muted: var(--color-text-secondary, #697278);
  --publish-subtle: #8a9398;
  --publish-line: #e3e8e6;
  --publish-surface: #ffffff;
  --publish-canvas: #f5f7f6;
  --publish-brand: var(--color-primary, #087f5b);
  --publish-brand-deep: #056044;
  --publish-danger: #bd5b5b;
  width: calc(100% - 48px);
  max-width: 1440px;
  margin: 0 auto;
  padding: 36px 0 136px;
  color: var(--publish-ink);
}

.product-publish__header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 28px;
  margin-bottom: 32px;
}

.product-publish__header h1 {
  margin: 0;
  color: var(--publish-ink);
  font-size: clamp(32px, 3vw, 42px);
  font-weight: 800;
  letter-spacing: -0.04em;
  line-height: 1.1;
}

.product-publish__intro {
  max-width: 54ch;
  margin: 10px 0 0;
  color: var(--publish-muted);
  font-size: 15px;
  line-height: 1.65;
}

.product-publish__header-actions,
.publish-footer__actions,
.inline-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.product-switcher {
  position: relative;
  min-width: 230px;
}

.product-switcher summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 40px;
  padding: 0 14px;
  border: 1px solid var(--publish-line);
  border-radius: 8px;
  background: var(--publish-surface);
  color: var(--publish-ink);
  cursor: pointer;
  font-size: 13px;
  list-style: none;
}

.product-switcher summary::-webkit-details-marker {
  display: none;
}

.product-switcher[open] summary {
  border-color: var(--publish-brand);
  box-shadow: 0 0 0 3px rgb(8 127 91 / 10%);
}

.product-switcher__menu {
  position: absolute;
  z-index: 5;
  top: calc(100% + 8px);
  right: 0;
  width: min(420px, 80vw);
  max-height: 420px;
  overflow: auto;
  padding: 10px;
  border: 1px solid var(--publish-line);
  border-radius: 10px;
  background: var(--publish-surface);
  box-shadow: 0 18px 40px rgb(19 42 34 / 15%);
}

.product-switcher__title {
  margin: 4px 8px 8px;
  color: var(--publish-muted);
  font-size: 12px;
  font-weight: 700;
}

.product-switcher__item {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 10px 8px;
  border: 0;
  border-radius: 7px;
  background: transparent;
  color: var(--publish-ink);
  text-align: left;
}

.product-switcher__item:hover,
.product-switcher__item--active {
  background: #eef7f3;
}

.product-switcher__select {
  display: flex;
  min-width: 0;
  flex: 1;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
  text-align: left;
}

.product-switcher__select strong,
.product-switcher__select small {
  display: block;
}

.product-switcher__select strong {
  font-size: 12px;
}

.product-switcher__select small {
  max-width: 230px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-top: 3px;
  color: var(--publish-muted);
  font-size: 13px;
}

.product-switcher__meta {
  flex-shrink: 0;
  color: var(--publish-muted);
  font-size: 11px;
}

.product-switcher__delete {
  flex-shrink: 0;
  padding: 4px 2px 4px 10px;
  border: 0;
  background: transparent;
  color: #b42318;
  cursor: pointer;
  font-size: 12px;
}

.product-switcher__delete:hover {
  color: #8f1d14;
  text-decoration: underline;
}

.product-switcher__delete:disabled,
.product-switcher__select:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.notice {
  margin: 0 0 18px;
  padding: 12px 14px;
  border: 1px solid #b9dfd0;
  border-radius: 8px;
  background: #f0faf6;
  color: var(--publish-brand-deep);
  font-size: 13px;
  line-height: 1.5;
}

.product-publish__content {
  display: grid;
  gap: 18px;
}

.entry-card {
  padding: 32px;
  border: 1px solid var(--publish-line);
  border-radius: 16px;
  background: var(--publish-surface);
  box-shadow: 0 4px 18px rgb(21 47 39 / 4%);
}

.entry-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 26px;
}

.entry-card__header h2 {
  margin: 0;
  color: var(--publish-ink);
  font-size: 24px;
  font-weight: 800;
  letter-spacing: -0.02em;
}

.entry-card__hint {
  padding-top: 5px;
  color: var(--publish-muted);
  font-size: 13px;
}

.field-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px 20px;
}

.field-grid--compact {
  gap: 14px 16px;
}

.field {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 9px;
  color: var(--publish-muted);
  font-size: 13px;
  font-weight: 700;
}

.field > span {
  display: block;
}

.field__hint {
  margin: -2px 0 0;
  color: var(--publish-muted);
  font-size: 11px;
  font-weight: 500;
  line-height: 1.5;
}

.field b {
  color: var(--publish-brand);
  font-weight: 800;
}

.field--wide {
  grid-column: 1 / -1;
}

.field--number {
  max-width: 150px;
}

.field input,
.field select,
.field textarea {
  width: 100%;
  min-height: 46px;
  box-sizing: border-box;
  padding: 0 12px;
  border: 1px solid var(--publish-line);
  border-radius: 7px;
  outline: none;
  background: #fbfcfc;
  color: var(--publish-ink);
  font: inherit;
  font-size: 14px;
  font-weight: 500;
  transition: border-color 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
}

.field textarea {
  min-height: 118px;
  padding: 11px 12px;
  resize: vertical;
}

.field__code {
  font-family: ui-monospace, SFMono-Regular, Consolas, "Liberation Mono", monospace !important;
  font-size: 13px !important;
  line-height: 1.6;
}

.field input:focus,
.field select:focus,
.field textarea:focus {
  border-color: var(--publish-brand);
  background: var(--publish-surface);
  box-shadow: 0 0 0 3px rgb(8 127 91 / 10%);
}

.field input.popular-toggle {
  width: 20px;
  min-height: 20px;
  height: 20px;
  padding: 0;
  accent-color: var(--publish-brand);
}

.field input:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.field--compact {
  gap: 6px;
  font-size: 11px;
}

.field--compact input,
.field--compact select {
  min-height: 36px;
  padding: 0 9px;
  font-size: 12px;
}

.field--legacy {
  justify-content: flex-start;
}

.field--legacy p {
  min-height: 42px;
  box-sizing: border-box;
  margin: 0;
  padding: 12px;
  border: 1px dashed var(--publish-line);
  border-radius: 7px;
  color: var(--publish-muted);
  font-size: 12px;
  font-weight: 500;
}

.button {
  display: inline-flex;
  min-height: 44px;
  align-items: center;
  justify-content: center;
  padding: 0 18px;
  border: 1px solid transparent;
  border-radius: 7px;
  background: var(--publish-brand);
  color: #ffffff;
  cursor: pointer;
  font: inherit;
  font-size: 14px;
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
  transition: background 0.18s ease, border-color 0.18s ease, transform 0.18s ease;
}

.button:hover:not(:disabled) {
  background: var(--publish-brand-deep);
}

.button:active:not(:disabled) {
  transform: translateY(1px);
}

.button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.button--primary {
  background: var(--publish-brand);
}

.button--secondary {
  border-color: #b9dfd0;
  background: #f0faf6;
  color: var(--publish-brand-deep);
}

.button--secondary:hover:not(:disabled) {
  background: #e3f5ed;
}

.button--ghost {
  border-color: var(--publish-line);
  background: #ffffff;
  color: var(--publish-ink);
}

.button--ghost:hover:not(:disabled) {
  border-color: #b9dfd0;
  background: #f7fbf9;
}

.button--danger {
  border-color: #e6c5c5;
  background: #fff7f7;
  color: var(--publish-danger);
}

.button--danger:hover:not(:disabled) {
  background: #ffefef;
}

.button--small {
  min-height: 32px;
  padding: 0 9px;
  border-radius: 6px;
  font-size: 11px;
}

.media-upload {
  display: grid;
  gap: 18px;
  padding-bottom: 22px;
  border-bottom: 1px solid var(--publish-line);
}

.media-gallery {
  display: grid;
  gap: 14px;
}

.media-gallery__heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  color: var(--publish-muted);
  font-size: 12px;
}

.media-gallery__heading strong,
.media-gallery__heading span {
  display: block;
}

.media-gallery__heading strong {
  color: var(--publish-ink);
  font-size: 14px;
}

.media-gallery__heading div span {
  margin-top: 4px;
}

.media-upload__actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.media-upload__actions > span {
  color: var(--publish-muted);
  font-size: 12px;
}

.media-upload__preview {
  display: grid;
  grid-template-columns: repeat(5, minmax(150px, 1fr));
  gap: 16px;
}

.media-add-tile {
  position: relative;
  display: flex;
  aspect-ratio: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 7px;
  border: 1px dashed #9abeb1;
  border-radius: 10px;
  background: #f7fbf9;
  color: var(--publish-brand-deep);
  cursor: pointer;
  text-align: center;
}

.media-add-tile:hover {
  border-color: var(--publish-brand);
  background: #eef8f3;
}

.media-add-tile input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
}

.media-add-tile__plus {
  display: grid;
  width: 34px;
  height: 34px;
  place-items: center;
  border-radius: 50%;
  background: #dff2ea;
  font-size: 24px;
  font-weight: 400;
  line-height: 1;
}

.media-add-tile strong {
  font-size: 13px;
}

.media-add-tile small {
  color: var(--publish-muted);
  font-size: 11px;
}

.media-pending {
  display: flex;
  aspect-ratio: 1;
  box-sizing: border-box;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 18px;
  padding: 14px;
  border: 1px solid var(--publish-line);
  border-radius: 10px;
  background: #fbfcfc;
}

.media-pending img {
  width: 100%;
  height: 72%;
  min-height: 0;
  border-radius: 8px;
  background: #f0f3f2;
  object-fit: contain;
}

.media-pending strong,
.media-pending span {
  display: block;
}

.media-pending strong {
  font-size: 12px;
}

.media-pending span {
  margin-top: 4px;
  color: var(--publish-muted);
  font-size: 10px;
}

.media-upload__fields {
  display: grid;
  grid-template-columns: minmax(160px, 0.8fr) minmax(220px, 1.4fr) 100px auto;
  align-items: end;
  gap: 14px;
}

.media-upload__fields .button {
  min-height: 36px;
}

.media-library__heading,
.sku-editor__header,
.editor-block__heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
}

.media-library {
  display: grid;
  gap: 14px;
  padding-top: 22px;
}

.media-library__heading {
  color: var(--publish-muted);
  font-size: 12px;
}

.media-library__heading strong,
.media-library__heading span {
  display: block;
}

.media-library__heading strong {
  color: var(--publish-ink);
  font-size: 14px;
}

.media-library__heading span {
  margin-top: 4px;
}

.media-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 16px;
}

.media-grid--product {
  align-items: start;
}

.media-tile {
  min-width: 0;
  overflow: hidden;
  border: 1px solid var(--publish-line);
  border-radius: 9px;
  background: #ffffff;
}

.media-tile--main {
  border-color: #8fcdbb;
  box-shadow: 0 0 0 2px #e4f6ee;
}

.media-tile--dragging {
  opacity: 0.55;
}

.media-tile--drop-target {
  border-color: var(--publish-brand);
  box-shadow: 0 0 0 2px #dff2ea;
}

.media-tile__image {
  position: relative;
  aspect-ratio: 1;
  background: #f4f6f5;
}

.media-tile__image img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.media-tile__main-marker {
  position: absolute;
  top: 8px;
  left: 8px;
  padding: 4px 6px;
  border-radius: 4px;
  background: var(--publish-brand);
  color: #ffffff;
  font-size: 10px;
  line-height: 1;
}

.media-tile__delete {
  position: absolute;
  top: 6px;
  right: 6px;
  display: grid;
  width: 28px;
  height: 28px;
  place-items: center;
  border: 0;
  border-radius: 50%;
  background: rgb(255 255 255 / 92%);
  color: var(--publish-ink);
  cursor: pointer;
  font-size: 20px;
  line-height: 1;
}

.media-tile__delete:hover:not(:disabled) {
  background: #ffefef;
  color: var(--publish-danger);
}

.media-tile__delete:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.media-tile__status {
  position: absolute;
  top: 7px;
  right: 7px;
  padding: 3px 5px;
  border-radius: 4px;
  background: rgb(255 255 255 / 88%);
  color: var(--publish-muted);
  font-size: 9px;
}

.media-tile__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 7px;
  padding: 9px 9px 0;
  color: var(--publish-muted);
  font-size: 10px;
}

.media-tile__header strong {
  color: var(--publish-ink);
  font-size: 12px;
}

.media-tile__editor {
  margin: 8px 9px 9px;
  border-top: 1px solid var(--publish-line);
}

.media-tile__editor summary {
  padding: 8px 0 5px;
  color: var(--publish-muted);
  cursor: pointer;
  font-size: 10px;
  list-style: none;
}

.media-tile__editor summary::-webkit-details-marker {
  display: none;
}

.media-tile__fields {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 66px;
  gap: 8px;
}

.media-tile__editor .field input {
  min-height: 30px;
  padding: 0 7px;
  font-size: 11px;
}

.media-tile__editor .inline-actions {
  gap: 5px;
  margin-top: 8px;
}

.media-empty {
  grid-column: 1 / -1;
  margin: 0;
  padding: 24px 12px;
  border: 1px dashed var(--publish-line);
  border-radius: 8px;
  color: var(--publish-muted);
  font-size: 12px;
  text-align: center;
}

.detail-card__header {
  align-items: center;
  margin-bottom: 20px;
}

.detail-card__content {
  display: grid;
  gap: 24px;
}

.detail-sections-editor {
  display: grid;
  gap: 0;
  padding: 0;
}

.detail-section-editor-list {
  display: grid;
  gap: 12px;
}

.detail-section-editor {
  display: grid;
  gap: 12px;
  padding: 4px 0 0;
}

.detail-section-tabs {
  display: flex;
  flex: 0 1 auto;
  gap: 22px;
  padding: 0;
  border: 0;
  border-radius: 0;
  background: transparent;
  overflow-x: auto;
}

.detail-section-tabs__tab {
  flex: 0 0 auto;
  min-width: 0;
  padding: 8px 2px 11px;
  border: 0;
  border-bottom: 2px solid transparent;
  border-radius: 0;
  background: transparent;
  color: var(--publish-muted);
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
}

.detail-section-tabs__tab:hover,
.detail-section-tabs__tab:focus-visible {
  color: var(--publish-brand);
  outline: none;
}

.detail-section-tabs__tab--active {
  border-bottom-color: var(--publish-brand);
  background: transparent;
  color: var(--publish-brand);
  box-shadow: none;
}

.detail-block-draft__summary {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: start;
  gap: 10px;
}

.detail-block-draft__summary-content {
  min-width: 0;
}

.detail-block-draft__summary-content strong,
.detail-block-draft__summary-content p {
  display: block;
}

.detail-block-draft__summary-content strong {
  color: var(--publish-ink);
  font-size: 13px;
}

.detail-block-draft__summary-content p {
  margin: 5px 0 0;
  overflow: hidden;
  color: var(--publish-muted);
  font-size: 12px;
  line-height: 1.5;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.detail-block-draft__media {
  display: grid;
  gap: 10px;
}

.detail-block-draft__preview--empty {
  display: grid;
  min-height: 120px;
  place-items: center;
  color: var(--publish-muted);
  font-size: 12px;
}

.detail-block-draft__fields {
  display: grid;
  gap: 10px;
}

.detail-block-editor {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 220px));
  align-items: start;
  gap: 16px 18px;
  justify-content: start;
}

.detail-block-editor__toolbar {
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-bottom: 2px;
}

.detail-block-editor__toolbar > span {
  max-width: 560px;
}

.detail-block-editor__upload {
  position: relative;
  overflow: hidden;
}

.detail-block-editor__upload input {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  cursor: pointer;
  opacity: 0;
}

.detail-block-editor__upload:has(input:disabled) {
  cursor: not-allowed;
  opacity: 0.5;
}

.detail-block-draft {
  display: grid;
  gap: 10px;
  min-width: 0;
  padding: 12px 0;
  border-bottom: 1px solid #e5ece9;
}

.detail-block-draft:not(.detail-block-draft--image) {
  grid-column: 1 / -1;
}

.detail-block-draft--image {
  position: relative;
  gap: 0;
  padding: 0;
  border-bottom: 0;
  cursor: grab;
  transition: opacity 160ms ease, transform 160ms ease;
}

.detail-block-draft--image:active {
  cursor: grabbing;
}

.detail-block-draft--image-dragging {
  opacity: 0.48;
  transform: scale(0.98);
}

.detail-block-draft--image-drop-target .detail-block-draft__preview {
  box-shadow: 0 0 0 2px var(--publish-brand);
}

.detail-block-draft__actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.detail-block-draft__actions {
  justify-content: flex-end;
  padding-top: 2px;
}

.detail-block-draft__preview {
  width: 100%;
  height: 160px;
  overflow: hidden;
  border: 1px solid #e5ece9;
  border-radius: 10px;
  background: #f6f8f7;
}

.detail-block-draft__preview img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.detail-block-draft--image .detail-block-draft__media {
  min-width: 0;
}

.detail-block-draft--image .detail-block-draft__preview--empty {
  min-height: 160px;
}

.detail-block-draft--image .detail-block-draft__actions {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1;
  padding-top: 0;
}

.detail-block-draft--image .detail-block-draft__actions .button {
  padding: 4px 8px;
  border-color: rgba(255, 255, 255, 0.9);
  background: rgba(255, 255, 255, 0.92);
}

.detail-block-draft__remove-image {
  display: grid;
  width: 28px;
  height: 28px;
  place-items: center;
  padding: 0;
  border: 1px solid rgba(255, 255, 255, 0.96);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.94);
  box-shadow: 0 2px 8px rgba(27, 52, 43, 0.14);
  color: var(--publish-muted);
  font-size: 20px;
  font-weight: 400;
  line-height: 1;
  cursor: pointer;
}

.detail-block-draft__remove-image:hover,
.detail-block-draft__remove-image:focus-visible {
  border-color: #e6c5c5;
  color: var(--publish-danger);
  outline: none;
}

.specs-setup {
  display: grid;
  gap: 0;
  border: 1px solid var(--publish-line);
  border-radius: 9px;
  background: #fbfcfc;
}

.specs-setup--legacy {
  display: none;
}

.specs-setup__summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 18px;
  cursor: pointer;
  list-style: none;
}

.specs-setup__summary::-webkit-details-marker {
  display: none;
}

.specs-setup__summary::after {
  content: "＋";
  margin-left: auto;
  color: var(--publish-brand);
  font-size: 18px;
  font-weight: 400;
}

.specs-setup[open] .specs-setup__summary::after {
  content: "－";
}

.specs-setup__summary strong {
  font-size: 13px;
}

.specs-setup__summary span {
  color: var(--publish-muted);
  font-size: 11px;
}

.specs-setup__body {
  display: grid;
  gap: 16px;
  padding: 0 16px 16px;
  border-top: 1px solid var(--publish-line);
}

.configured-attributes {
  margin: 0;
  padding: 10px 12px;
  border-radius: 6px;
  background: #eef8f3;
  color: var(--publish-brand-deep);
  font-size: 12px;
}

.attribute-form {
  display: grid;
  grid-template-columns: minmax(180px, 0.8fr) minmax(240px, 1.2fr) auto;
  align-items: end;
  gap: 12px;
}

.sku-create-form {
  display: flex;
  align-items: end;
  gap: 12px;
  padding-top: 2px;
}

.sku-create-form .field {
  min-width: 220px;
}

.sku-table {
  display: grid;
  margin-top: 22px;
  border: 1px solid var(--publish-line);
  border-radius: 9px;
  overflow: hidden;
}

.sku-groups {
  display: grid;
  margin-top: 22px;
  overflow: hidden;
  border: 1px solid var(--publish-line);
  border-radius: 9px;
}

.sku-group {
  padding: 20px 22px;
  border-bottom: 1px solid var(--publish-line);
  background: #ffffff;
}

.sku-group:last-of-type {
  border-bottom: 0;
}

.sku-group__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
}

.sku-group__identity {
  display: grid;
  min-width: 0;
  gap: 7px;
}

.sku-group__label,
.sku-tier-header {
  color: var(--publish-muted);
  font-size: 12px;
  font-weight: 700;
}

.sku-group__name {
  display: grid;
  min-width: 0;
  gap: 5px;
}

.sku-group__spec-input,
.sku-group__draft-name {
  display: block;
  width: min(100%, 520px);
}

.sku-group__spec-input input,
.sku-group__draft-name input,
.sku-tier-quantity input,
.sku-tier-price input {
  box-sizing: border-box;
  min-width: 0;
  border: 1px solid var(--publish-line);
  border-radius: 6px;
  background: #ffffff;
  color: var(--publish-ink);
  font: inherit;
}

.sku-group__spec-input input,
.sku-group__draft-name input {
  width: 100%;
  padding: 9px 11px;
  font-size: 16px;
  font-weight: 700;
}

.sku-group__name small {
  color: var(--publish-muted);
  font-size: 12px;
}

.sku-group__delete {
  flex: 0 0 auto;
  padding: 6px 0;
  border: 0;
  background: transparent;
  color: var(--publish-danger);
  cursor: pointer;
  font: inherit;
  font-size: 12px;
  font-weight: 700;
}

.sku-group__delete:disabled,
.sku-tier-actions .button:disabled,
.sku-tier-add:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.sku-group__draft-status {
  flex: 0 0 auto;
  color: var(--publish-brand-deep);
  font-size: 12px;
  font-weight: 700;
}

.sku-tier-header,
.sku-tier-row {
  display: grid;
  grid-template-columns: minmax(180px, 1fr) minmax(180px, 1fr) minmax(150px, auto);
  align-items: center;
  gap: 16px;
}

.sku-tier-header {
  margin-top: 18px;
  padding-bottom: 8px;
}

.sku-tier-row {
  min-height: 54px;
  padding: 9px 0;
  border-top: 1px solid var(--publish-line);
}

.sku-tier-quantity,
.sku-tier-price {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 8px;
}

.sku-tier-quantity input,
.sku-tier-price input {
  width: min(100%, 180px);
  padding: 9px 10px;
  font-size: 15px;
}

.sku-tier-price {
  color: var(--publish-brand);
  font-size: 17px;
  font-weight: 800;
}

.sku-tier-price input {
  color: var(--publish-ink);
  font-weight: 700;
}

.sku-tier-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}

.sku-tier-row--draft {
  margin-top: 10px;
  padding: 12px;
  border: 1px dashed var(--publish-line);
  border-radius: 7px;
  background: #fbfdfb;
}

.sku-draft-package-settings {
  display: grid;
  grid-column: 1 / -1;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  align-items: end;
}

.sku-draft-package-settings .field__hint {
  grid-column: 1 / -1;
}

.sku-draft-package-settings__locked {
  grid-column: 1 / -1;
  margin: 0;
}

.sku-draft-error {
  grid-column: 1 / -1;
  margin: 0;
  color: var(--publish-danger);
  font-size: 12px;
  font-weight: 700;
}

.sku-tier-add {
  display: block;
  width: 100%;
  margin-top: 12px;
  padding: 8px 0;
  border: 0;
  background: transparent;
  color: var(--publish-brand-deep);
  cursor: pointer;
  font: inherit;
  font-size: 12px;
  font-weight: 800;
  text-align: left;
}

.sku-group--draft {
  background: #fbfdfb;
}

.specs-card__title {
  display: flex;
  align-items: center;
  gap: 10px;
}

.specs-card__icon {
  display: inline-flex;
  width: 26px;
  height: 26px;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  border: 2px solid var(--publish-brand);
  border-radius: 5px;
  color: var(--publish-brand);
  font-size: 16px;
  font-weight: 900;
  line-height: 1;
}

.custom-config {
  display: grid;
  gap: 18px;
}

.custom-config__notice,
.custom-config__empty,
.custom-config__hint {
  margin: 0;
  color: var(--publish-muted);
  font-size: 12px;
  line-height: 1.5;
}

.custom-config__create {
  display: grid;
  gap: 14px;
  padding: 16px;
  border: 1px dashed var(--publish-line);
  border-radius: 9px;
  background: #fbfdfb;
}

.custom-config__create-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
}

.custom-presets {
  display: grid;
  gap: 14px;
  padding: 16px;
  border: 1px solid #b9dfd0;
  border-radius: 9px;
  background: #f7fbf9;
}

.custom-presets__list {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}

.custom-preset {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 9px;
  padding: 10px;
  border: 1px solid var(--publish-line);
  border-radius: 7px;
  background: #ffffff;
  cursor: pointer;
}

.custom-preset--applied {
  border-color: #b9dfd0;
  background: #eef8f3;
  cursor: default;
}

.custom-preset__check {
  display: inline-grid;
  width: 15px;
  height: 15px;
  flex: 0 0 15px;
  place-items: center;
  border-radius: 3px;
  background: var(--publish-brand);
  color: #ffffff;
  font-size: 11px;
  font-weight: 800;
}

.custom-preset__content {
  display: grid;
  min-width: 0;
  gap: 3px;
}

.custom-preset__content strong,
.custom-preset__content small {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.custom-preset__content strong {
  color: var(--publish-ink);
  font-size: 12px;
}

.custom-preset__content small {
  color: var(--publish-muted);
  font-size: 10px;
}

.custom-presets__actions {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--publish-muted);
  font-size: 11px;
}

.custom-config__subheader {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  color: var(--publish-ink);
  font-size: 13px;
}

.custom-config__subheader span {
  color: var(--publish-muted);
  font-size: 11px;
  font-weight: 500;
}

.custom-config__fields {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px 14px;
}

.custom-config__dimension-fields {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  grid-column: 1 / -1;
  gap: 10px 16px;
  padding-top: 2px;
}

.custom-config__dimension-fields strong {
  color: var(--publish-ink);
  font-size: 12px;
}

.custom-config__required {
  align-self: end;
  padding-bottom: 9px;
}

.custom-config__add {
  justify-self: start;
}

.custom-config__groups {
  display: grid;
  gap: 14px;
}

.custom-group__bundle-heading {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-top: 4px;
  padding: 0 2px;
  color: var(--publish-ink);
}

.custom-group__bundle-heading strong {
  font-size: 14px;
}

.custom-group__bundle-heading span {
  color: var(--publish-muted);
  font-size: 11px;
}

.custom-group {
  display: grid;
  gap: 14px;
  padding: 18px;
  border: 1px solid #b9dfd0;
  border-radius: 9px;
  background: #ffffff;
}

.custom-group--inactive {
  border-color: var(--publish-line);
  background: #fbfcfc;
}

.custom-group--print-config {
  border-color: #b9dfd0;
  background: #f7fbf9;
}

.custom-group__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.custom-group__identity {
  min-width: 0;
}

.custom-group__header-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}

.custom-group__expand {
  min-width: 56px;
}

.custom-group__body {
  display: grid;
  gap: 14px;
}

.custom-group__title {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.custom-group__title strong {
  font-size: 15px;
}

.custom-group__header small {
  display: block;
  margin-top: 5px;
  color: var(--publish-muted);
  font-size: 11px;
}

.custom-group__actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.custom-options {
  display: grid;
  gap: 9px;
  padding-top: 14px;
  border-top: 1px solid var(--publish-line);
}

.custom-option {
  display: grid;
  grid-template-columns: minmax(160px, 200px) minmax(0, 1fr) 140px auto auto auto auto;
  align-items: center;
  gap: 8px;
}

.custom-option:has(.custom-option__image) {
  grid-template-columns: 42px minmax(160px, 200px) minmax(0, 1fr) 140px auto auto auto auto;
}

.custom-option__image {
  width: 42px;
  height: 42px;
  border: 1px solid var(--publish-line);
  border-radius: 6px;
  background: #f4f6f5;
  object-fit: cover;
}

.custom-option__upload {
  position: relative;
  overflow: hidden;
}

.custom-option__upload input {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  cursor: pointer;
  opacity: 0;
}

.custom-option input {
  box-sizing: border-box;
  min-width: 0;
  width: 100%;
  padding: 7px 8px;
  border: 1px solid var(--publish-line);
  border-radius: 6px;
  background: #ffffff;
  color: var(--publish-ink);
  font: inherit;
  font-size: 12px;
}

.custom-option--inactive {
  opacity: 0.66;
}

.custom-option--new {
  padding-top: 9px;
  border-top: 1px dashed var(--publish-line);
}

.packaging-settings__fields {
  display: grid;
  grid-template-columns: minmax(180px, 0.8fr) minmax(240px, 1.2fr);
  gap: 16px;
}

.product-service-note {
  padding: 10px 12px;
  border: 1px solid var(--publish-line);
  border-radius: 8px;
  background: #f7faf8;
}

.product-service-note__label {
  display: block;
  color: var(--publish-ink);
  font-size: 12px;
  font-weight: 700;
}

.product-service-note p {
  margin: 6px 0 0;
  color: var(--publish-muted);
  font-size: 12px;
  line-height: 1.55;
}

.packaging-settings__count-input {
  display: flex;
  align-items: center;
  gap: 8px;
}

.packaging-settings__count-input input {
  min-width: 0;
  flex: 1;
}

.packaging-settings__count-input span {
  color: var(--publish-muted);
}

.packaging-settings__message {
  margin: 14px 0 0;
  color: var(--publish-muted);
  font-size: 12px;
  line-height: 1.5;
}

.packaging-settings__message--error {
  color: var(--publish-danger);
}

.product-parameters__save {
  justify-self: start;
}

.product-parameters .field input:disabled {
  background: #f2f6f4;
  color: var(--publish-muted);
}

.sku-table__row {
  display: grid;
  grid-template-columns: minmax(260px, 2fr) minmax(150px, 1fr) minmax(180px, 1.2fr) minmax(72px, 0.35fr);
  align-items: center;
  gap: 12px;
  width: 100%;
  box-sizing: border-box;
  padding: 17px 18px;
  border: 0;
  border-bottom: 1px solid var(--publish-line);
  background: #ffffff;
  color: var(--publish-ink);
  font: inherit;
  font-size: 13px;
  text-align: left;
}

.sku-table__row:last-child {
  border-bottom: 0;
}

.sku-table__row--head {
  background: #f6f8f7;
  color: var(--publish-muted);
  font-size: 12px;
  font-weight: 700;
}

.sku-table__row--item {
  cursor: default;
}

.sku-table__row--item:hover {
  background: #f3faf7;
}

.sku-table__name strong,
.sku-table__name small {
  display: block;
}

.sku-table__name {
  display: grid;
  align-content: center;
  justify-items: start;
  gap: 6px;
  min-width: 0;
}

.sku-table__spec-input {
  display: block;
  width: min(100%, 420px);
}

.sku-table__spec-input input,
.quantity-input input {
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  border: 1px solid var(--publish-line);
  border-radius: 6px;
  background: #ffffff;
  color: var(--publish-ink);
  font: inherit;
}

.sku-table__spec-input input {
  padding: 7px 9px;
  font-size: 14px;
  font-weight: 700;
}

.sku-table__name-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.sku-table__name-actions > form {
  display: flex;
}

.sku-table__name strong {
  font-size: 14px;
}

.sku-table__simple-quantity,
.sku-table__simple-price {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  color: var(--publish-ink);
}

.sku-table__simple-quantity input,
.sku-table__simple-price input {
  width: min(100%, 160px);
  min-width: 0;
  box-sizing: border-box;
  border: 1px solid var(--publish-line);
  border-radius: 6px;
  background: #ffffff;
  color: var(--publish-ink);
  font: inherit;
  font-size: 15px;
}

.sku-table__simple-quantity input {
  padding: 10px 11px;
}

.sku-table__simple-price {
  color: var(--publish-brand);
  font-size: 17px;
  font-weight: 800;
}

.sku-table__simple-price input {
  padding: 10px 11px;
  color: var(--publish-ink);
  font-weight: 700;
}

.sku-table__delete {
  justify-self: end;
  padding: 8px;
  border: 0;
  background: transparent;
  color: var(--publish-danger);
  cursor: pointer;
  font-size: 20px;
  line-height: 1;
}

.sku-table__delete:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.spec-add-inline {
  display: block;
  width: 100%;
  box-sizing: border-box;
  padding: 17px 18px;
  border: 1px solid var(--publish-line);
  border-top: 0;
  background: #ffffff;
  color: var(--publish-brand);
  cursor: pointer;
  font: inherit;
  font-size: 15px;
  font-weight: 800;
  text-align: center;
}

.spec-add-inline:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.sku-table__row--draft {
  background: #fbfdfb;
}

.sku-table__draft-name {
  width: min(100%, 420px);
}

.sku-table__draft-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}

.sku-table__draft-save,
.sku-table__draft-delete {
  padding: 4px 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  font: inherit;
  font-size: 12px;
  font-weight: 700;
}

.sku-table__draft-save {
  color: var(--publish-brand);
}

.sku-table__draft-delete {
  color: var(--publish-danger);
}

.sku-table__draft-save:disabled,
.sku-table__draft-delete:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.sku-table__name small {
  margin-top: 4px;
  color: var(--publish-muted);
  font-size: 12px;
  font-weight: 500;
}

.sku-table__tiers,
.sku-table__prices {
  display: grid;
  gap: 5px;
  line-height: 1.35;
}

.sku-table__tiers span,
.sku-table__prices span {
  display: block;
}

.sku-table__tiers .sku-table__tier-row {
  display: flex;
  align-items: center;
  gap: 5px;
  min-height: 32px;
  padding-bottom: 5px;
  border-bottom: 1px solid var(--publish-line);
  white-space: nowrap;
}

.sku-table__tiers .sku-table__tier-row:last-child,
.sku-table__prices .sku-table__price-row:last-child,
.sku-table__tier-actions .sku-table__tier-action-row:last-child {
  padding-bottom: 0;
  border-bottom: 0;
}

.quantity-input {
  display: inline-flex;
  width: 62px;
  flex: 0 0 62px;
}

.quantity-input input {
  padding: 6px 7px;
  font-size: 13px;
}

.sku-table__prices {
  color: var(--publish-brand-deep);
  font-weight: 800;
}

.sku-table__price-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  min-height: 32px;
  padding-bottom: 5px;
  border-bottom: 1px solid var(--publish-line);
}

.sku-table__tier-actions {
  display: grid;
  gap: 5px;
  min-width: 0;
}

.sku-table__tier-action-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  min-height: 32px;
  padding-bottom: 5px;
  border-bottom: 1px solid var(--publish-line);
}

.sku-table__tier-action-row > form {
  display: flex;
}

.tier-add-inline {
  grid-column: 1 / -1;
  overflow: hidden;
  border: 1px dashed var(--publish-line);
  border-radius: 7px;
  background: #fbfcfc;
}

.tier-add-inline summary {
  padding: 8px 10px;
  cursor: pointer;
  color: var(--publish-brand-deep);
  font-size: 12px;
  font-weight: 800;
  list-style: none;
}

.tier-add-inline summary::-webkit-details-marker {
  display: none;
}

.tier-add-inline summary::after {
  content: "＋";
  float: right;
  font-size: 15px;
  font-weight: 400;
}

.tier-add-inline[open] summary::after {
  content: "－";
}

.tier-add-inline__form {
  display: grid;
  grid-template-columns: minmax(130px, 1fr) minmax(130px, 1fr) auto;
  align-items: end;
  gap: 8px;
  padding: 10px;
  border-top: 1px solid var(--publish-line);
}

.status-pill,
.check-status {
  display: inline-flex;
  width: fit-content;
  align-items: center;
  min-height: 24px;
  padding: 0 8px;
  border-radius: 999px;
  background: #eef1f0;
  color: var(--publish-muted);
  font-size: 10px;
  font-weight: 700;
}

.status-pill--muted {
  background: #f4f6f5;
  color: var(--publish-muted);
}

.status-pill--danger {
  background: #fff1f0;
  color: #b42318;
}

.sku-table__name .status-pill {
  margin-top: 7px;
}

.sku-table__edit {
  color: var(--publish-brand);
  font-size: 11px;
  font-weight: 800;
}

.sku-editor {
  display: grid;
  gap: 16px;
  margin-top: 0;
  padding: 22px;
  border-top: 1px solid #b9dfd0;
  background: #fbfefc;
}

.sku-editor-details {
  display: none;
  margin-top: 16px;
  overflow: hidden;
  border: 1px solid #b9dfd0;
  border-radius: 10px;
  background: #fbfefc;
}

.sku-editor-details__summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 20px;
  cursor: pointer;
  list-style: none;
}

.sku-editor-details__summary::-webkit-details-marker {
  display: none;
}

.sku-editor-details__summary h3,
.sku-editor-details__summary strong,
.sku-editor-details__summary span {
  display: block;
}

.sku-editor-details__summary h3 {
  margin: 0;
  font-size: 15px;
}

.sku-editor-details__summary strong {
  font-size: 14px;
}

.sku-editor-details__summary > div > span {
  margin-top: 5px;
  color: var(--publish-muted);
  font-size: 12px;
}

.sku-editor-details__summary > span {
  color: var(--publish-brand);
  font-size: 12px;
  font-weight: 700;
}

.sku-editor-details__summary > span::after {
  content: "＋";
  margin-left: 7px;
  font-size: 16px;
  font-weight: 400;
}

.sku-editor-details[open] .sku-editor-details__summary > span::after {
  content: "－";
}

.sku-editor__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding-bottom: 14px;
  border-bottom: 1px solid var(--publish-line);
}

.sku-editor__header h3 {
  margin: 0;
  font-size: 18px;
}

.sku-editor__header span {
  display: block;
  margin-top: 5px;
  color: var(--publish-muted);
  font-size: 12px;
}

.editor-block {
  display: grid;
  gap: 14px;
  padding-top: 2px;
}

.editor-block + .editor-block {
  padding-top: 16px;
  border-top: 1px solid var(--publish-line);
}

.editor-block__heading strong,
.editor-block__heading span {
  display: block;
}

.editor-block__heading strong {
  font-size: 13px;
}

.editor-block__heading span {
  margin-top: 4px;
  color: var(--publish-muted);
  font-size: 11px;
}

.sku-daily-editor {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.sku-daily-editor__row {
  display: grid;
  gap: 5px;
  padding: 12px 14px;
  border: 1px solid var(--publish-line);
  border-radius: 8px;
  background: #ffffff;
}

.sku-daily-editor__row > span {
  color: var(--publish-muted);
  font-size: 11px;
  font-weight: 700;
}

.sku-daily-editor__row strong {
  color: var(--publish-ink);
  font-size: 14px;
}

.sku-daily-editor__row small {
  color: var(--publish-muted);
  font-size: 11px;
}

.tier-compact-editor {
  display: grid;
  gap: 12px;
  padding-top: 2px;
}

.tier-compact-row {
  display: grid;
  grid-template-columns: minmax(180px, 1fr) minmax(130px, 0.7fr) auto auto auto;
  align-items: center;
  gap: 10px;
  padding: 9px 12px;
  border: 1px solid var(--publish-line);
  border-radius: 8px;
  background: #ffffff;
  color: var(--publish-ink);
  font-size: 12px;
}

.tier-compact-row > form {
  display: flex;
}

.price-input {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  color: var(--publish-brand-deep);
  font-size: 14px;
  font-weight: 800;
}

.price-input input {
  width: 92px;
  min-height: 32px;
  padding: 0 8px;
  border: 1px solid var(--publish-line);
  border-radius: 6px;
  color: var(--publish-ink);
  font: inherit;
  font-size: 13px;
  font-weight: 700;
}

.tier-compact-row__status {
  color: var(--publish-muted);
  font-size: 11px;
}

.tier-add,
.sku-editor__advanced {
  overflow: hidden;
  border: 1px solid var(--publish-line);
  border-radius: 8px;
  background: #ffffff;
}

.tier-add summary,
.sku-editor__advanced summary {
  padding: 12px 14px;
  cursor: pointer;
  color: var(--publish-brand-deep);
  font-size: 12px;
  font-weight: 800;
  list-style: none;
}

.tier-add summary::-webkit-details-marker,
.sku-editor__advanced summary::-webkit-details-marker {
  display: none;
}

.tier-add summary::after,
.sku-editor__advanced summary::after {
  content: "＋";
  float: right;
  font-size: 15px;
  font-weight: 400;
}

.tier-add[open] summary::after,
.sku-editor__advanced[open] summary::after {
  content: "－";
}

.tier-add__form,
.sku-editor__advanced-body {
  display: grid;
  gap: 12px;
  padding: 14px;
  border-top: 1px solid var(--publish-line);
}

.tier-add__form {
  grid-template-columns: minmax(140px, 0.8fr) minmax(140px, 0.8fr) auto;
  align-items: end;
}

.sku-editor__advanced-body {
  background: #fbfcfc;
}

.check-field {
  display: flex;
  min-height: 36px;
  align-items: center;
  gap: 8px;
  color: var(--publish-ink);
  font-size: 12px;
  font-weight: 600;
}

.check-field input {
  width: 16px;
  height: 16px;
  accent-color: var(--publish-brand);
}

.unit-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.unit-chip {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px 6px 10px;
  border: 1px solid var(--publish-line);
  border-radius: 6px;
  background: #ffffff;
  color: var(--publish-muted);
  font-size: 11px;
}

.publish-check__empty {
  margin: 0;
  padding: 14px;
  border-radius: 8px;
  background: #f6f8f7;
  color: var(--publish-muted);
  font-size: 12px;
}

.publish-check__groups {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.check-group {
  padding: 11px 13px;
  border: 1px solid var(--publish-line);
  border-radius: 8px;
  background: #fbfcfc;
}

.check-group--passed {
  border-color: #b9dfd0;
  background: #f4fbf7;
}

.check-group summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  cursor: pointer;
  color: var(--publish-ink);
  font-size: 12px;
  font-weight: 700;
  list-style: none;
}

.check-group summary::-webkit-details-marker {
  display: none;
}

.check-group summary strong {
  color: var(--publish-brand);
  font-size: 10px;
}

.check-group:not([open]) summary::after {
  content: "＋";
  color: var(--publish-brand);
  font-weight: 400;
}

.check-group ul {
  display: grid;
  gap: 6px;
  margin: 10px 0 0;
  padding-left: 16px;
  color: var(--publish-muted);
  font-size: 11px;
  line-height: 1.5;
}

.check-status--passed {
  background: #e3f5ed;
  color: var(--publish-brand-deep);
}

.check-status--pending {
  background: #f0f2f1;
  color: var(--publish-muted);
}

.publish-footer {
  position: fixed;
  z-index: 10;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  min-height: 72px;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 12px max(24px, calc((100vw - 1080px) / 2)) 12px;
  border-top: 1px solid var(--publish-line);
  background: rgb(255 255 255 / 96%);
  box-shadow: 0 -8px 24px rgb(21 47 39 / 6%);
  backdrop-filter: blur(10px);
}

.publish-footer__context {
  min-width: 0;
}

.publish-footer__context span,
.publish-footer__context strong {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.publish-footer__context span {
  color: var(--publish-muted);
  font-size: 11px;
}

.publish-footer__context strong {
  max-width: 360px;
  margin-top: 4px;
  color: var(--publish-ink);
  font-size: 13px;
}

.sr-only {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  clip-path: inset(50%) !important;
}

@media (max-width: 900px) {
  .sku-tier-header,
  .sku-tier-row {
    grid-template-columns: minmax(150px, 1fr) minmax(150px, 1fr) minmax(120px, auto);
  }

  .product-publish {
    width: min(100% - 32px, 720px);
    padding-top: 22px;
  }

  .product-publish__header {
    align-items: flex-start;
    flex-direction: column;
  }

  .product-publish__header-actions {
    width: 100%;
  }

  .product-switcher {
    flex: 1;
  }

  .media-upload__fields {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .media-upload__fields .button {
    width: 100%;
  }

  .attribute-form {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .attribute-form .button {
    width: fit-content;
  }

  .sku-table__row {
    grid-template-columns: minmax(190px, 1.5fr) 120px minmax(150px, 1fr) 72px;
  }

  .publish-footer {
    padding-right: 16px;
    padding-left: 16px;
  }
}

@media (max-width: 640px) {
  .sku-group {
    padding: 16px;
  }

  .sku-group__header {
    flex-direction: column;
    gap: 10px;
  }

  .sku-tier-header {
    display: none;
  }

  .sku-tier-row {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 10px;
  }

  .sku-tier-actions {
    grid-column: 1 / -1;
    justify-content: flex-start;
  }

  .product-publish {
    width: min(100% - 24px, 560px);
    padding-bottom: 154px;
  }

  .entry-card {
    padding: 18px;
    border-radius: 11px;
  }

  .field-grid,
  .media-upload__preview,
  .media-upload__fields,
  .packaging-settings__fields,
  .custom-config__fields,
  .attribute-form,
  .publish-check__groups {
    grid-template-columns: 1fr;
  }

  .field--wide {
    grid-column: auto;
  }

  .media-pending {
    min-height: 124px;
  }

  .media-pending img {
    width: 92px;
    height: 92px;
  }

  .media-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .sku-table {
    overflow-x: auto;
  }

  .sku-table__row {
    min-width: 680px;
  }

  .sku-editor {
    padding: 14px;
  }

  .custom-group {
    padding: 14px;
  }

  .custom-group__header {
    align-items: stretch;
    flex-direction: column;
  }

  .custom-group__header-actions {
    justify-content: flex-start;
  }

  .custom-config__create-header,
  .custom-group__bundle-heading {
    align-items: flex-start;
    flex-direction: column;
  }

  .custom-option {
    grid-template-columns: minmax(160px, 200px) minmax(0, 1fr) 140px;
  }

  .custom-option:has(.custom-option__image) {
    grid-template-columns: 42px minmax(160px, 200px) minmax(0, 1fr) 140px;
  }

  .detail-card__header,
  .detail-block-editor__toolbar {
    align-items: stretch;
    flex-direction: column;
  }

  .detail-section-tabs__tab {
    flex: 0 0 auto;
  }

  .custom-option .status-pill,
  .custom-option .button {
    justify-self: start;
  }

  .sku-editor__header {
    align-items: stretch;
    flex-direction: column;
  }

  .sku-daily-editor,
  .tier-add__form,
  .tier-add-inline__form,
  .spec-add-inline__form {
    grid-template-columns: 1fr;
  }

  .tier-compact-row {
    grid-template-columns: minmax(0, 1fr) auto;
  }

  .tier-compact-row > form,
  .tier-compact-row > .button,
  .tier-compact-row__status {
    grid-column: 2;
  }

  .publish-footer {
    align-items: stretch;
    flex-direction: column;
    gap: 10px;
    padding-top: 10px;
  }

  .publish-footer__actions {
    width: 100%;
  }

  .publish-footer__actions .button {
    flex: 1;
  }
}
</style>
